import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import MainPage from "../MainPage";

/**
 * PredictionMarketsWave4Page — /highlights/prediction-markets
 *
 * THE prediction markets reel. It began as a wave-aware rebuild alongside the
 * original single-snapshot one, and now replaces it: this component is what
 * /highlights/prediction-markets serves, and it is the only prediction markets
 * story in stories.js. The filename still says Wave4 for git continuity.
 *
 * PredictionMarketsPage.js is no longer routed or imported anywhere. It is kept
 * on disk only because its slide-2 and title-page markup were the source for
 * this reel's, and it can be deleted once nobody wants to diff against it.
 *
 * Three things distinguish it from the original: the regulation question is a
 * TRACKED series, so the reel shows movement rather than a single snapshot;
 * wave 4 added questions that did not exist before; and the legal position the
 * numbers speak to has changed completely.
 *
 * ── PROVENANCE ───────────────────────────────────────────────────────────────
 * All figures below are weighted by `total` off the tracking tables, computed
 * from tracking/state/wave={3,4} and tracking/cd_summary/wave={3,4}. Wave 3 is
 * April 2026, wave 4 is July 2026, each n>8,000 US adults, MRP estimates.
 *
 * IMPORTANT, and the reason the old reel's numbers all still check out: the
 * March/April 2026 megapoll that the original reel read from the `single`
 * schema was folded into tracking as WAVE 3. The wave-3 tracking toplines
 * (15.1/32.9/17.6/8.9/25.6) reproduce the old page's hardcoded PM_OPTS
 * (15/33/18/8/26) exactly, and the wave-3 district rankings reproduce its
 * REG_FILTERS copy exactly (VA-08/CO-02/NY-12/WA-07 on "same as gambling",
 * IA-02/IA-04/ME-02/NC-11/PA-09 on "ban"). Nothing was lost in the move; wave 4
 * is genuinely additive.
 *
 * ── WHAT THE TRACKED SERIES SHOWS ────────────────────────────────────────────
 * The country consolidated around regulating prediction markets somehow, and
 * split on which frame. Both regulated options gained, both unregulated-extreme
 * options lost:
 *   ban entirely       15.1 -> 12.7  (-2.4)
 *   same as gambling   32.9 -> 35.2  (+2.3)
 *   different          17.6 -> 19.4  (+1.8)
 *   no regulation       8.9 ->  7.8  (-1.1)
 *   not sure           25.6 -> 24.9  (-0.7)
 *   regulate somehow (same+diff) 50.4 -> 54.6  (+4.2)
 *   ban or nothing   (ban+none)  24.0 -> 20.5  (-3.5)
 *
 * ── THE STRONGEST DEMOGRAPHIC RESULT: THE GENERATIONS SPLIT APART ────────────
 * The two ends of the age range moved in OPPOSITE directions on the same
 * question, which is why the national totals look calm and the subgroups do not.
 *   18-34 "different from gambling" 22.1 -> 27.5  (+5.4)
 *   65+   "different from gambling" 14.4 -> 11.4  (-3.0)
 *   the gap between them            7.7  -> 16.1  (more than doubled in a quarter)
 *   65+   "same as gambling"        31.4 -> 38.0  (+6.6, the largest single move)
 *
 * A partisan CROSSOVER also opened on "ban entirely". Hard opposition collapsed
 * among Trump voters and held among Harris voters, so by wave 4 Harris voters
 * are MORE likely to want prediction markets banned than Trump voters:
 *   Trump  15.3 -> 11.5  (-3.8)
 *   Harris 15.9 -> 15.6  (-0.3)
 *   did not vote 13.7 -> 10.1  (-3.6)
 * Note this cuts against the bipartisan framing of the Curtis/Schiff bill.
 *
 * ── WAVE 4 ONLY: WHO SHOULD REGULATE (the question the courts are deciding) ──
 *   both, sharing responsibility  35.7  (73.8M adults)
 *   a single federal regulator    23.6  (48.8M)
 *   individual states             20.7  (42.8M)
 *   don't know                    16.6  (34.3M)
 *   no one, largely unregulated    3.3  ( 6.7M)
 * Federal-inclusive (federal + both) 59.4 vs state-inclusive (states + both)
 * 56.5, so the two coalitions are within 3 points of each other. "Both" is the
 * plurality in ALL 436 districts; federal-only beats state-only in 342 of 436.
 * The platforms' own legal position (exclusive federal jurisdiction) is the
 * minority view at 23.6, and the public's plurality answer is not an option
 * anyone is litigating for.
 * Counterintuitive age result: the YOUNG are the most pro-state (18-34 at 28.3
 * on individual states vs 15.6 for 65+), while 65+ are the most pro-federal.
 *
 * ── WAVE 4 ONLY: THE ENGAGEMENT LADDER ───────────────────────────────────────
 * Modelled as a mutually exclusive partition (the seven shares sum to 1.0 on
 * every row; see the note in gambling_aggregators.js). Shares of all adults:
 *   had not heard of them        25.1  (51.7M)
 *   have only heard of them      22.3  (46.1M)
 *   saw news coverage citing odds 16.1  (33.1M)
 *   checked odds without trading 11.1  (22.8M)
 *   discussed prediction markets 10.3  (21.3M)
 *   used a play-money site        8.2  (17.0M)
 *   traded with real money        7.0  (14.4M)
 *   name-only (last two rows)    47.4  (97.8M)
 * Because the categories do not overlap, the honest comparison is between
 * groups, not up a funnel: 2.3x as many adults sit in the news-coverage
 * category as in the traded category. Never-heard runs 15.4 to 32.5 across
 * districts, and 42.5 among over-65s against 10.2 among under-35s.
 *
 * ── UTAH, WHICH IS THE REASON THIS REEL EXISTS ───────────────────────────────
 * Utah is the most opinionated state in the country on prediction markets, and
 * it is moving in two directions at once. Ranks are out of 51 (50 states + DC):
 *   "different from gambling"  16.4 -> 21.5  (+5.1)  LARGEST RISE of any state
 *                              level rank 2/51, and 1st of the 50 states
 *   "not sure"                 23.2 -> 20.7  (-2.5)  LOWEST in the country (51/51)
 *   "ban entirely"             15.1 -> 14.6  (-0.4)  HIGHEST in the country (1/51)
 *   "individual states" (W4)          23.6           HIGHEST in the country (1/51)
 *   "no regulation"     (W4)           2.2           50th of 51
 * All four Utah districts moved the same way on "different from gambling":
 *   UT-01 16.4 -> 21.2 · UT-02 16.3 -> 20.8 · UT-03 16.0 -> 21.1 · UT-04 16.9 -> 22.8
 * UT-04 is 4th highest of all 436 districts. Utah adults: 1.72M.
 * Read it as engagement, not contradiction: Utah has thought about this more
 * than anywhere else, and the thinking is hardening at both ends.
 *
 * ── THE LEGAL MAP IS NOT AN OPINION MAP (a finding, and a warning) ───────────
 * Grouping states by how their courts ruled produces almost no difference:
 *   ruled AGAINST platforms (UT NV OH MD NY WI, 31.6M): same 33.0->35.1,
 *     diff 17.6->19.7, ban 15.1->12.7 | fed 23.6 state 20.6 both 36.1
 *   ruled FOR platforms (NJ TN AZ MN, 18.6M): same 33.6->35.6,
 *     diff 17.0->19.4, ban 15.1->12.7 | fed 23.3 state 20.8 both 36.2
 *   Ninth Circuit states (39.3M): same 32.1->35.3, diff 19.0->20.0,
 *     ban 15.1->13.4 | fed 26.1 state 20.8 both 35.3
 * Litigation outcomes are tracking legal geography, not public opinion. Do not
 * let anyone read a court result off this map, in either direction.
 *
 * ── WHAT IS DELIBERATELY OFF THE REEL ────────────────────────────────────────
 * Everything above stays documented here because it is verified and will come
 * up in the room. It is off the slides because it is not a decision Polymarket
 * has to make this quarter:
 *   - The "ban entirely" collapse (430 of 436 districts). Real, and uniform,
 *     but a ban is not the live threat: being classed as gambling is, and that
 *     is a different question with a different answer.
 *   - The engagement ladder (47.4% have not got past the name). The best
 *     targeting map in the set and the strongest growth argument, but it is a
 *     comms brief, not a legal one. Worth its own slide the moment the
 *     conversation turns from litigation to acquisition.
 *   - Utah. The news hook, and the biggest state mover on the frame at +5.1,
 *     but 1.7M adults and the case is already lost. Kept to a line.
 *   - The litigation-map-is-not-an-opinion-map result. A null finding, which
 *     is worth saying out loud in conversation and wastes a slide.
 *
 * ── CAVEATS THAT MUST TRAVEL WITH THESE NUMBERS ──────────────────────────────
 * 1. DISTRICT MOVEMENT IS NOW LIKE-FOR-LIKE. Both waves are modelled on the
 *    SAME district boundaries, verified rather than assumed: the two waves
 *    carry an identical set of 436 dist_fips with identical per-state counts.
 *    Per-seat deltas are therefore real. (An earlier build reported movement
 *    only as distribution counts because waves 3 and 4 sat on different
 *    vintages; that restriction is gone.) On 436 districts:
 *      "ban entirely" fell in 430, rose in 5, mean -2.37, sd 1.06
 *      districts with "ban" under 12%:      0 (April) -> 104 (July)
 *      "different" rose in 388, mean +1.81, sd 1.57
 *      districts with "different" over 20%: 46 -> 144
 *      districts with "different" over 25%:  0 ->   0   (a hard ceiling)
 *      districts that changed their winning answer: 8, all of them between
 *        "not sure" and "same as gambling"
 *    TWO SHAPES WORTH KNOWING. The ban collapse was UNIFORM: r between a
 *    district's April level and its change is -0.05, so the whole distribution
 *    slid down together rather than gaps opening. "Different from gambling"
 *    CONVERGED: r = -0.70, with the lowest-starting third gaining +2.9 against
 *    +0.6 for the highest-starting third. The laggards closed on the leaders,
 *    and the former leaders in Hawaii actually fell (HI-01 24.6 -> 20.1).
 *    Only five districts saw hard opposition GROW, and the list is striking:
 *    MN-05 (Minneapolis) +1.4, WA-07 (Seattle) +1.4, CA-11 (San Francisco)
 *    +0.9, UT-01 +0.6, UT-03 +0.5. Three of the most progressive metros in the
 *    country, plus two Utah seats. It lines up with the vote24 result above,
 *    where a ban held among Harris voters while collapsing among Trump voters.
 *    Biggest risers on "different": TX-03 +6.0, UT-04 +5.9, TX-26 +5.8,
 *    VA-08 +5.8, TX-37 +5.4. Texas averaged +2.3 against California's +0.7.
 * 2. `party` IS NOT TRACKABLE. It is coded four-way in wave 3 (~90.9M "none")
 *    and three-way leaned in wave 4 (~69/66/71M). Use vote24, which is
 *    identical in both waves (77.1M Trump, 74.8M Harris, 3.1M other, ~51M did
 *    not vote). Every partisan figure here is vote24.
 * 3. `econ_approve` IS NOT CLEANLY TRACKABLE INTO WAVE 4 EITHER, which is why
 *    this reel does not open on it the way the original did. Wave 4 added a
 *    fourth `pol_vote:econapprove_dk:lik` column taking 3.8%, so the three
 *    original columns sum to 96.2 in wave 4 against 100.0 in wave 3, and the
 *    apparent disapproval fall (50.5 -> 43.4) is partly the new option
 *    absorbing share. polAggregators still declares econ_approve as waves
 *    [1,2,3]; extending it needs the dk column handled first.
 * 4. These are MODELLED estimates, not measured subgroup counts. Local and
 *    demographic figures come out of MRP, so they must never be presented as
 *    observed responses. This matters more than usual here: if any of it ends
 *    up in an amicus brief or a legislative hearing it will be read
 *    adversarially.
 * 5. Wave 4 carries an empty placeholder row for DC in the district tables
 *    (dist_fips 1100, all nulls) alongside the real 1198, which inflates a
 *    naive count to 437. All 436-district counts above exclude it.
 * 6. The three questions are SEPARATE MEASURES. Regulation preference, who
 *    should regulate, and experience must not be combined into a single
 *    favourability score.
 */

// ─── Data constants (see PROVENANCE header) ───────────────────────────────────
// Wave labels live in the copy now that no control picks a wave: every map is
// either a change map or a wave-4 question.

// Wave labels for the title slide's toggle. econ_approve now carries wave 4
// (it gained a "don't know" option in that wave; see polAggregators).
const WAVE_LABELS = { 1: "Wave 1", 2: "Wave 2", 3: "Wave 3", 4: "Wave 4 (latest)" };

// The geography toggle on the title slide, lifted from the original reel.
const INTRO_GEO_LEVELS = [
  { key: "states",       label: "State" },
  { key: "cd",           label: "CD" },
  { key: "media_market", label: "Media market" },
];

// ── Slide 2: the regulatory landscape, on WAVE 4 ────────────────────────────
// Ported from the original reel, with every figure and district list recomputed
// on wave 4 rather than the March/April snapshot the original quoted.
//
// The cluster notes deliberately describe GEOGRAPHY, which the district
// estimates support, and avoid the demographic composition claims the original
// made ("liberal, highly educated", "majority-Hispanic"). Those were not
// measured here, and on modelled estimates built partly from demographics they
// would be close to circular.
// The "biggest gain" view, which is MapPage's own compare-waves mode rather
// than anything computed here: compareWaves + compareKey "__top__" colours each
// district by whichever response gained most between the two latest waves, and
// shades it by the size of that gain. getLatestTwoWaves reads waveOptions,
// which setHighlightsState fills from the group's declared waves [3, 4], so the
// pair is April to July without this reel having to name the waves.
//
// Counts below are measured, and they are why the view is worth having: the
// winner is not the same everywhere. Magnitudes run 1.2 to 6.9 points, so the
// shading has something to work with too.
const REG_GAIN_LEGEND = [
  { label: "Same as gambling",       color: "#F77F00", districts: 239 },
  { label: "Different from gambling", color: "#4CC9F0", districts: 185 },
  { label: "Not sure",               color: "#94a3b8", districts: 12  },
];

const REG_FILTERS = [
  {
    key: "prediction_markets__same_gambling_pred_n", label: "Same as gambling", color: "#F77F00", pct: 35.2,
    sublabel: "The default instinct, and a plurality rather than a majority",
    cluster: "Highest in NC-04 and NC-02 across North Carolina's Research Triangle, KS-03 in the Kansas City suburbs, FL-19 on Florida's retirement coast and CO-02 around Boulder, all near 40%. University metros, affluent suburbs and retiree communities are not an obvious coalition, and that is the point: the instinct to treat these markets as gambling is not confined to one political or cultural type.",
  },
  {
    key: "prediction_markets__dk_pred_n", label: "Not sure", color: "#94a3b8", pct: 24.9,
    sublabel: "No settled view yet",
    cluster: "Highest in IL-02 south of Chicago, GA-02 in southwest Georgia, MI-13 in Detroit, MS-02 in the Mississippi Delta and AL-04 in northern Alabama, all around 30%. Lower-income urban and rural districts where the question has not really arrived. Uncertainty at this level is more likely to mean unfamiliarity than opposition, which makes these the places where a first argument has the most room.",
  },
  {
    key: "prediction_markets__diff_gambling_pred_n", label: "Different from gambling", color: "#4CC9F0", pct: 19.4,
    sublabel: "Open to a separate regulatory category",
    cluster: "Strongest in NY-07 and NY-10 across Brooklyn and Lower Manhattan, TX-37 in Austin, CO-01 in Denver and UT-04 in South Salt Lake, at 23 to 24%. Dense, young, high-information urban districts, plus Utah. These are the voters most willing to treat prediction markets as their own category rather than as betting, though even here it is a minority position: no district in the country passes 25%.",
  },
  {
    key: "prediction_markets__banned_pred_n", label: "Ban entirely", color: "#C1121F", pct: 12.7,
    sublabel: "Hardest opposition, and smaller than it sounds",
    cluster: "Highest in MN-05 in Minneapolis, WA-07 in Seattle, CA-11 in San Francisco and UT-01 and UT-03 in northern and central Utah, at 15 to 16%. Two very different routes to the same answer: consumer-protection scepticism in progressive metros, and long-standing objection to gambling in Utah. Hard opposition is small everywhere and reaches 20% in none of the 436 districts.",
  },
  {
    key: "prediction_markets__no_regulation_pred_n", label: "No regulation", color: "#06D6A0", pct: 7.8,
    sublabel: "The most permissive view, and the rarest",
    cluster: "Highest in MS-02 in the Mississippi Delta, GA-05 and GA-13 in and around Atlanta, LA-06 in Baton Rouge and MD-04 in Prince George's County, at 10 to 12%. The smallest group by a distance, and worth knowing mainly for its size: almost nobody anywhere wants these markets left alone entirely.",
  },
];

// ── The two frames, as the two actual shares ─────────────────────────────────
// Deliberately NOT a derived margin. An earlier build showed (different - same)
// as one signed number and it was unreadable: always negative, and "gap to
// what?" is not answerable at a glance. These are the two real percentages,
// April and July, and the movement speaks for itself.
const FRAME_OPTS = [
  { key: "same", label: "Same as gambling",        color: "#F77F00", mapKey: "prediction_markets__same_gambling_pred_n" },
  { key: "diff", label: "Different from gambling", color: "#4CC9F0", mapKey: "prediction_markets__diff_gambling_pred_n" },
];


// Small multiples of the DISTRICT DISTRIBUTION, one row per group on a shared
// axis. This replaced a colour-only treatment that could not do both jobs at
// once: a map pinned to a shared scale compares groups honestly but flattens
// the variation inside each one, and a map fitted to each group shows the
// variation but makes the groups incomparable. Splitting them into one strip
// per group solves it structurally rather than by tuning a ramp: position on
// the shared axis carries the comparison, and the box and whiskers carry every
// district's spread.
//
// Each entry is [min, p25, median, p75, max] of the 436 district estimates in
// July, plus April's median so the shift shows in the same encoding rather than
// mixing a mean into a median-based chart. Pulled straight from the loader.
//
// The headline result is how little the groups overlap on "different from
// gambling": over-65s top out at 15.3 and under-35s start at 23.5, so the two
// distributions do not touch.
const FRAME_DIST = {
  diff: {
    axis: [9, 32],
    "All adults":   { jul: [15.8, 18.5, 19.4, 20.3, 23.7], aprMed: 17.4 },
    "18–34":        { jul: [23.5, 26.8, 27.5, 28.3, 30.8], aprMed: 22.1 },
    "35–64":        { jul: [18.4, 19.8, 20.5, 21.3, 23.4], aprMed: 17.6 },
    "65+":          { jul: [10.0, 10.8, 11.3, 12.1, 15.3], aprMed: 13.7 },
    "Trump":        { jul: [17.2, 19.6, 21.0, 22.8, 29.3], aprMed: 17.5 },
    "Harris":       { jul: [13.9, 17.2, 17.8, 18.5, 21.9], aprMed: 16.0 },
    "Did not vote": { jul: [15.5, 18.2, 19.2, 20.8, 24.8], aprMed: 19.5 },
  },
  same: {
    axis: [24, 46],
    "All adults":   { jul: [30.0, 33.8, 35.1, 36.4, 40.7], aprMed: 33.0 },
    "18–34":        { jul: [30.4, 33.6, 34.7, 35.6, 39.8], aprMed: 35.4 },
    "35–64":        { jul: [28.1, 32.4, 33.8, 35.1, 39.6], aprMed: 32.6 },
    "65+":          { jul: [32.3, 36.3, 37.9, 39.3, 44.4], aprMed: 31.8 },
    "Trump":        { jul: [30.7, 32.9, 33.7, 34.9, 38.0], aprMed: 32.4 },
    "Harris":       { jul: [29.6, 36.5, 38.4, 39.6, 44.5], aprMed: 36.6 },
    "Did not vote": { jul: [25.6, 31.1, 32.7, 34.5, 39.7], aprMed: 28.5 },
  },
};

// Biggest per-district movers, April to July, per option. `id` is the CD
// area_id (2-digit state FIPS + 2-digit district), which is the key the map
// highlights on, so these rows are clickable rather than decorative.
// Both waves sit on the same district boundaries, so these are like-for-like.
const FRAME_MOVERS = {
  diff: {
    up: [
      { id: "4803", name: "TX-03", region: "Plano / Collin County",  w3: 14.8, w4: 20.8 },
      { id: "4904", name: "UT-04", region: "South Salt Lake",        w3: 16.9, w4: 22.8 },
      { id: "4826", name: "TX-26", region: "Denton / Flower Mound",  w3: 15.6, w4: 21.4 },
      { id: "5108", name: "VA-08", region: "Arlington / Alexandria", w3: 16.1, w4: 21.9 },
      { id: "4837", name: "TX-37", region: "Austin",                 w3: 17.8, w4: 23.2 },
    ],
    down: [
      { id: "1501", name: "HI-01", region: "Honolulu",            w3: 24.6, w4: 20.1 },
      { id: "1502", name: "HI-02", region: "Rural Hawaii",        w3: 22.0, w4: 18.3 },
      { id: "2404", name: "MD-04", region: "Prince George's",     w3: 22.9, w4: 19.8 },
      { id: "2802", name: "MS-02", region: "Mississippi Delta",   w3: 20.7, w4: 17.7 },
      { id: "1313", name: "GA-13", region: "Atlanta south",       w3: 20.3, w4: 18.1 },
    ],
  },
  same: {
    up: [
      { id: "3615", name: "NY-15", region: "South Bronx",        w3: 23.8, w4: 30.7 },
      { id: "2206", name: "LA-06", region: "Baton Rouge",        w3: 28.1, w4: 34.9 },
      { id: "0642", name: "CA-42", region: "Long Beach",         w3: 29.0, w4: 35.7 },
      { id: "0625", name: "CA-25", region: "San Fernando north", w3: 27.4, w4: 34.1 },
      { id: "1218", name: "FL-18", region: "Central Florida",    w3: 30.1, w4: 36.8 },
    ],
    down: [
      { id: "4803", name: "TX-03", region: "Plano / Collin County",  w3: 37.7, w4: 33.6 },
      { id: "4705", name: "TN-05", region: "Nashville",              w3: 36.7, w4: 33.9 },
      { id: "4826", name: "TX-26", region: "Denton / Flower Mound",  w3: 36.6, w4: 33.8 },
      { id: "5108", name: "VA-08", region: "Arlington / Alexandria", w3: 39.6, w4: 37.0 },
      { id: "5111", name: "VA-11", region: "Fairfax",                w3: 39.1, w4: 36.9 },
    ],
  },
};

const FRAME_CUTS = [
  {
    key: "age", label: "Age",
    note: "The two age extremes do not overlap at all on \"different from gambling\": every district sits above 23.5 among under-35s and below 15.3 among over-65s. Over-65s are also the only group that moved the other way.",
    rows: ["18–34", "35–64", "65+"],
    filters: { "18–34": { age: 1 }, "35–64": { age: 2 }, "65+": { age: 3 } },
  },
  {
    key: "vote24", label: "2024 vote",
    note: "Trump districts sit above Harris districts throughout, and moved further: a 3.5 point shift in the median against 1.8. Trump districts are also the widest-spread group of any, from 17.2 to 29.3.",
    rows: ["Trump", "Harris", "Did not vote"],
    filters: { "Trump": { vote24: 1 }, "Harris": { vote24: 2 }, "Did not vote": { vote24: 4 } },
  },
];

// Texas seats that moved most on "different from gambling". Real percentages,
// April to July, on the same district boundaries in both waves.
const TEXAS_MOVERS = [
  { name: "TX-03", region: "Plano / Collin County",  vals: [14.8, 20.8] },
  { name: "TX-26", region: "Denton / Flower Mound",  vals: [15.6, 21.4] },
  { name: "TX-37", region: "Austin",                 vals: [17.8, 23.2] },
  { name: "TX-04", region: "East Texas",             vals: [15.4, 20.2] },
];

// The question's five actual answers, as asked. These are real response shares
// and they partition the sample: 35.7 + 23.6 + 20.7 + 16.6 + 3.3 = 99.9, the
// rounding. No derived or combined figures here.
//
// Colours are the pm_regulate map palette (see gambling_aggregators.js), so a
// bar and the district it colours on the map are the same hue.
const WHO_REGULATES = [
  { label: "Both, sharing responsibility",  pct: 35.7, color: "#4a3aa7" },
  { label: "A single federal regulator",    pct: 23.6, color: "#2a78d6" },
  { label: "Individual states",             pct: 20.7, color: "#eb6834" },
  { label: "Don't know",                    pct: 16.6, color: "#78716c" },
  { label: "No one, largely unregulated",   pct: 3.3,  color: "#1baf7a" },
];

// The same second choice, cut by group. The flip is the finding: the people
// most likely to use a prediction market are the least federalist about it.
const SECOND_BY_GROUP = [
  { label: "All adults", second: "Federal regulator", pct: 23.6, color: "#2a78d6" },
  { label: "18–34",      second: "Individual states", pct: 28.3, color: "#eb6834" },
  { label: "No degree",  second: "Individual states", pct: 21.2, color: "#eb6834" },
  { label: "Degree",     second: "Federal regulator", pct: 28.7, color: "#2a78d6" },
  { label: "65+",        second: "Federal regulator", pct: 25.6, color: "#2a78d6" },
];

// Texas, wave 4. The three facts that make it the commercially live state:
// scale, actual usage, and a legislature that the public leans toward.
const TEXAS_STATS = [
  {
    stat: "2nd", color: "#4e79a7",
    label: "largest audience in the country",
    body: "Behind only California, and unlike California it has a legislature convening in 2027 with prediction markets already discussed as a gambling loophole.",
  },
  {
    stat: "22 of 38", color: "#F77F00",
    label: "districts lean state over federal",
    body: "Texas is one of only seven states where \"individual states should regulate\" outpolls \"a single federal regulator\". The margin is thin statewide, at 21.6% against 21.3%, but it runs the wrong way for a federal-preemption argument in most of the state.",
  },
];

// The next ROUND OF FIELDWORK, not advice. Same discipline as the data centres
// reel: every one of these is a question THIS study raises and cannot answer.
//
// What the reel measures is the position people already hold: which regulatory
// label they pick, who they think should hold the pen, whether they call it
// gambling. What it never does is ask WHY they hold it, describe what a regime
// would actually contain, or ask who they would trust to run one. Those are the
// three gaps, and each is asked here at both national and local level, which is
// the part District Profiles adds.
const NEXT_STEPS = [
  {
    title: "What is driving the gambling comparison?",
    desc: "Understand which features of prediction markets make people see them as similar to, or different from, gambling, and how that varies across the country.",
  },
  {
    title: "What kind of regulation would people support?",
    desc: "Test specific rules and safeguards to see what a workable regulatory framework looks like to the public, nationally and by state or district.",
  },
  {
    title: "Who do people trust to oversee prediction markets?",
    desc: "Understand whether people place more confidence in federal regulators, states or other institutions, and where those preferences differ geographically.",
  },
];

// ─── Step definitions ─────────────────────────────────────────────────────────
// MAP LAYER CHOICE IS DELIBERATE. Every layer here was measured for district
// spread before being assigned, because a choropleth of a narrow share is a flat
// beige map however good the data is. p10-to-p90 across the 436 districts:
//   had not heard        8.6   <- widest, and geographically legible
//   single federal reg.  7.6
//   not sure             6.4
//   same as gambling     4.8
//   different            3.2   <- the reel's headline metric, and nearly flat
//   ban entirely         2.8
// So "different from gambling" carries the NARRATIVE but is only mapped where
// the interaction supplies the contrast (a wave flip, or a demographic recut),
// never as a static national choropleth.
const STEPS = [
  {
    name: "intro",
    headline: "One national survey. Local estimates across multiple geographies.",
    copy: "Stack surveys more than 8,000 American adults online each wave. The sample is balanced to be nationally representative by age, gender, ethnicity, education, party identification, and region.",
    // The title slide is the tool demo, so it maps economic approval rather than
    // the case-study question: one familiar measure the toggles can move across
    // four waves and three geographies.
    mapLabel: "Economic approval: net score by congressional district",
    mapView: { variable: "econ_approve", geoLevel: "cd" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 0 },
    showWaveToggle: true,
    showIntroGeoToggle: true,
  },
  {
    name: "regulation-appetite",
    headline: "No single regulatory view has a majority.",
    copy: "Asked how prediction markets should be regulated, \"same as gambling\" is the most common answer at 35.2%. That is a plurality, not a majority: the rest of the public is spread across the other approaches or still undecided.",
    mapLabel: "Prediction market regulation by congressional district",
    mapView: { variable: "prediction_markets_regulation", geoLevel: "cd", preferenceFilter: "prediction_markets__same_gambling_pred_n", wave: 4 },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    showRegFilter: true,
  },
  {
    name: "frame",
    headline: "Under-35s and over-65s moved in opposite directions.",
    copy: "Nationally both frames grew: \"same as gambling\" rose 2.3 points and \"different from gambling\" rose 1.8. Split by age and changes diverge. Among under-35s, \"different\" rose 5.4 while \"same\" slipped. Among over-65s, \"same\" rose 6.6 while \"different\" fell 3.0.",
    mapLabel: "% of adults by district",
    // Level for the selected wave, on a pinned domain, so switching group
    // re-colours honestly instead of re-fitting the ramp to that group.
    mapView: { variable: "prediction_markets_regulation", geoLevel: "cd", preferenceFilter: "prediction_markets__diff_gambling_pred_n", wave: 4 },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
  },
  {
    name: "who-regulates",
    headline: "Every district backs both federal and state involvement in prediction market regulation",
    copy: "\"Both should share responsibility\" is the most popular approach nationally at 35.7%, ahead of a single federal regulator (23.6%) and regulation by individual states (20.7%). Just 3.3% say prediction markets should be left unregulated.\nNo single option reaches a majority, so the map shows each district's second-most-popular response.",
    mapLabel: "Second choice for who should regulate",
    mapView: { variable: "pm_regulate", geoLevel: "cd", preferenceFilter: "second" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
  },
  {
    name: "texas",
    headline: "Texas has the most districts leaning to state regulation.",
    copy: "Second-largest audience in the country, third-highest real-money trading, and one of only seven states where \"individual states should regulate\" outpolls \"a single federal regulator\". Of the 73 districts nationally that put individual states second, 21 are Texan. The legislature convenes in 2027.",
    mapLabel: "Second choice for who should regulate, Texas",
    mapView: { variable: "pm_regulate", geoLevel: "cd", stateFilter: "48", preferenceFilter: "second" },
    flyTo: { center: [-99.0, 31.2], zoom: 5.4, duration: 1400 },
  },
  {
    name: "summary",
    headline: "Three things to take with you",
    copy: "",
    mapLabel: "\"Different from gambling\", % of adults by district",
    mapView: { variable: "prediction_markets_regulation", geoLevel: "cd", preferenceFilter: "prediction_markets__diff_gambling_pred_n", wave: 4 },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1400 },
    hero: true,
  },
];

const CHAPTER_NAMES = {
  "intro":               "The Tool & Methodology",
  "regulation-appetite": "The Regulatory Landscape",
  "frame":         "Gambling Or Not",
  "who-regulates": "Federal Or State",
  "texas":         "Texas",
  "summary":       "What To Do With This",
};

// The closing hero's three takeaways, in the reel's own order of confidence.
const TAKEAWAYS = [
  {
    title: "The generations are moving apart",
    desc: "Under-35s are increasingly likely to see prediction markets as different from gambling; over-65s are moving the other way. The national average hides that divergence.",
  },
  {
    title: "The ban debate is fading. The classification debate is not.",
    desc: "Support for an outright ban fell in 430 of 436 districts. But \"same as gambling\" still leads \"different from gambling\" nationally, and the gap widened slightly. The central question is increasingly how prediction markets should be classified and regulated.",
  },
  {
    title: "Shared regulation has the broadest support",
    desc: "\"Both should share responsibility\" is the top response nationally and in every district, ahead of either federal-only or state-only regulation.",
  },
];

const F = "'Inter', 'DM Sans', sans-serif";
const TEAL      = "#0d9488";
const TEAL_DARK = "#0f766e";
const TEAL_GLOW = "rgba(13,148,136,0.18)";
const ACCENT    = "#06b6d4";
const INK       = "#0f172a";
const MUTED     = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER     = "#ffffff";
const PAPER_SOFT = "linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER    = "rgba(13,148,136,0.1)";
const BG        = "#ffffff";

// ─── Animation helpers ────────────────────────────────────────────────────────
const FU_KF = {
  "@keyframes fuIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to:   { opacity: 1, transform: "translateY(0)" },
  },
};
const fu = (delay) => ({
  animation: `fuIn 0.52s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FU_KF,
});
const fuStagger = (base, i) => ({
  animation: `fuIn 0.5s cubic-bezier(0.22,1,0.36,1) ${(base + i * 0.09).toFixed(2)}s both`,
  ...FU_KF,
});

function CountUp({ value, isActive, duration = 900 }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!isActive) { setDisplay(value); return; }
    const m = String(value).match(/^([^0-9-]*)(-?\d+\.?\d*)([^0-9]*)$/);
    if (!m) { setDisplay(value); return; }
    const [, prefix, numStr, suffix] = m;
    const target = parseFloat(numStr);
    const hasDecimal = numStr.includes(".");
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const cur = target * ease;
      setDisplay(`${prefix}${hasDecimal ? cur.toFixed(1) : Math.round(cur)}${suffix}`);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive, value, duration]);
  return <>{display}</>;
}

// ─── Navigation dispatcher ────────────────────────────────────────────────────
let _cameraTimer = null;
let _lastFlyKey = "";

// navigateToStep skips the camera move when the next step's flyTo is identical
// to the last one applied, which saves a pointless re-fly between two slides
// that share a national view. That cache assumes nothing ELSE moved the camera.
// A district highlight does move it (MapPage zooms to the highlighted area), so
// the cache has to be invalidated or the next step change would decide it has
// nothing to do and leave the map parked on that district.
function invalidateFlyCache() {
  _lastFlyKey = "";
}

function getGlMap(mainInst) {
  return mainInst?._mapPageInstance?.mapRef?.current?.getMap?.() || null;
}

function exitHighlights(navigate) {
  if (window.innerWidth < 600) {
    try { sessionStorage.setItem("m.activeScreen", "map"); } catch (e) { /* ignore */ }
    navigate("/m");
  } else {
    navigate("/");
  }
}

function flyForViewport(fly) {
  return window.innerWidth < 600 ? { ...fly, padding: 0 } : fly;
}

function armCrossfade(glMap, duration = 550) {
  if (!glMap) return;
  let style;
  try { style = glMap.getStyle(); } catch { return; }
  if (!style?.layers) return;
  const t = { duration, delay: 0 };
  style.layers.forEach((layer) => {
    try {
      if (layer.type === "fill") {
        glMap.setPaintProperty(layer.id, "fill-color-transition", t);
        glMap.setPaintProperty(layer.id, "fill-opacity-transition", t);
        glMap.setPaintProperty(layer.id, "fill-outline-color-transition", t);
      } else if (layer.type === "line") {
        glMap.setPaintProperty(layer.id, "line-color-transition", t);
        glMap.setPaintProperty(layer.id, "line-opacity-transition", t);
      }
    } catch { /* safe */ }
  });
}

function navigateToStep(mainInst, stepName, extraMapState = {}) {
  if (!mainInst) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  if (_cameraTimer) { clearTimeout(_cameraTimer); _cameraTimer = null; }

  mainInst.setState({ chatHighlightAreas: step.highlights || [] });
  armCrossfade(getGlMap(mainInst));

  if (step.mapView) {
    mainInst.setHighlightsState({ ...step.mapView, ...extraMapState });
  }

  if (step.flyTo) {
    const key = JSON.stringify(step.flyTo);
    if (key !== _lastFlyKey) {
      _lastFlyKey = key;
      _cameraTimer = setTimeout(() => {
        _cameraTimer = null;
        mainInst.flyTo(flyForViewport(step.flyTo));
      }, 80);
    }
  }
}

// ─── Map toggles for the title slide ─────────────────────────────────────────
// Both sit over the map rather than in the card, so the title slide reads as a
// demo of the tool: change a control, watch the same measure redraw.
function IntroGeoToggle({ current, onChange }) {
  return (
    <Box sx={{ position: "absolute", bottom: 16, left: 16, zIndex: 10, display: "flex", borderRadius: "10px", overflow: "hidden", border: `1px solid ${TEAL}28`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
      {INTRO_GEO_LEVELS.map(({ key, label }) => {
        const active = current === key;
        return (
          <Box key={key} onClick={() => onChange(key)} sx={{ px: 1.4, py: 0.75, cursor: "pointer", bgcolor: active ? TEAL : "rgba(255,255,255,0.94)", backdropFilter: "blur(10px)", color: active ? "#fff" : MUTED, fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em", transition: "all 0.18s ease", userSelect: "none", "&:hover": { bgcolor: active ? TEAL_DARK : `${TEAL}0e` } }}>
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

function WaveToggle({ current, onChange }) {
  return (
    <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, display: "flex", borderRadius: "10px", overflow: "hidden", border: `1px solid ${TEAL}28`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
      {[1, 2, 3, 4].map((w) => {
        const active = current === w;
        return (
          <Box key={w} onClick={() => onChange(w)} sx={{ px: 1.4, py: 0.75, cursor: "pointer", bgcolor: active ? TEAL : "rgba(255,255,255,0.94)", backdropFilter: "blur(10px)", color: active ? "#fff" : MUTED, fontSize: "11px", fontWeight: 700, fontFamily: F, letterSpacing: "0.03em", transition: "all 0.18s ease", userSelect: "none", "&:hover": { bgcolor: active ? TEAL_DARK : `${TEAL}0e` } }}>
            {WAVE_LABELS[w] || `Wave ${w}`}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.4 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "20px", sm: "25px", md: "29px" }, fontWeight: 800, lineHeight: { xs: 1.22, md: 1.16 }, letterSpacing: "-0.03em", color: INK, fontFamily: F, mb: 1.6 }}>
      {children}
    </Typography>
  );
}

function IntroHeadline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "26px", sm: "31px", md: "35px" }, fontWeight: 800, lineHeight: { xs: 1.16, md: 1.1 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 1.7 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.8, fontFamily: F, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

// `...props` is load-bearing, not tidiness. The hero panels are rendered with
// data-hero-scroll="true", and the wheel handler looks for exactly that
// attribute (closest("[data-hero-scroll]")) to decide whether to let the panel
// scroll its own overflow instead of advancing the slide.
//
// This component was ported from SelfDrivingPage without the spread, so the
// attribute was dropped before it reached the DOM, the selector never matched,
// and the handler treated the summary panel as an ordinary slide: it called
// preventDefault, found it was already on the last slide, and returned. The
// result was a final card whose demo signup form could not be scrolled to at
// all. Removing the spread breaks that again silently.
function Panel({ children, sx = {}, ...props }) {
  return (
    <Box {...props} sx={{ width: "100%", p: { xs: 2, md: 2.5 }, borderRadius: "22px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(13,148,136,0.06)", ...sx }}>
      {children}
    </Box>
  );
}

function CardLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1 }}>
      {children}
    </Typography>
  );
}

function Note({ children }) {
  return (
    <Box sx={{ mt: 1.25, px: 1.5, py: 1.1, borderRadius: "12px", bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}18` }}>
      <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, lineHeight: 1.65 }}>{children}</Typography>
    </Box>
  );
}

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto", pt: 3.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>
          · {chapter}
        </Typography>
      )}
      {hasNext && (
        <Box onClick={onScrollNext} sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75, cursor: "pointer", px: 1.4, py: 0.75, borderRadius: "10px", border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}08`, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}14`, borderColor: `${TEAL}55` } }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, lineHeight: 1 }}>Scroll</Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      )}
    </Box>
  );
}

// ─── Map overlays ─────────────────────────────────────────────────────────────
// The Texas movers table, floated over the map rather than stacked in the card.
// It lives here because it is a READING of the map underneath it: the four
// districts named are visible in the same frame, so putting it in the left
// column made the reader look away from the thing being described.
//
// Positioned bottom-left. At the Texas slide's camera (centre -99.0/31.2, zoom
// 5.4) that corner is Big Bend and northern Mexico, the emptiest part of the
// frame, so the panel covers no district it is talking about. Hidden on xs,
// where the map strip is 40dvh and an overlay this size would bury it; the
// mobile reader gets the same numbers in the card instead.
// The movers rows themselves, shared by the map overlay (desktop) and the card
// (mobile), so the two can never drift apart.
function TexasMoversList() {
  return (
    <>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1 }}>
        "Different from gambling" · biggest Texas movers
      </Typography>
      {TEXAS_MOVERS.map((d) => (
        <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.55 }}>
          <Box sx={{ minWidth: 84 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1.15 }}>{d.name}</Typography>
            <Typography sx={{ fontSize: "8.5px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.2 }}>{d.region}</Typography>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: 11, minWidth: 40 }}>
            <Box sx={{ position: "absolute", inset: "3px 0", borderRadius: "3px", bgcolor: "rgba(76,201,240,0.15)" }} />
            <Box sx={{ position: "absolute", top: 3, bottom: 3, left: 0, width: `${(d.vals[1] / 26) * 100}%`, bgcolor: "#4CC9F0", borderRadius: "3px" }} />
            <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${(d.vals[0] / 26) * 100}%`, width: "2px", bgcolor: INK, opacity: 0.5 }} />
          </Box>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0b9fd0", fontFamily: F, minWidth: 38, textAlign: "right" }}>{d.vals[1].toFixed(1)}%</Typography>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#059669", fontFamily: F, minWidth: 32, textAlign: "right" }}>+{(d.vals[1] - d.vals[0]).toFixed(1)}</Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, mt: 0.7, fontStyle: "italic", lineHeight: 1.45 }}>
        TX-03 is the largest rise of any district in the country. Bar is July, tick is April.
      </Typography>
    </>
  );
}


// Legend for slide 2's "biggest gain" map. Needed because the colours stop
// meaning "share of this response" and start meaning "this response gained the
// most here", which is not inferable from the map.
function RegGainLegend() {
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 12,
      display: { xs: "none", sm: "block" },
      width: "min(320px, calc(100% - 32px))",
      px: 1.6, py: 1.3, borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(14px)",
      border: "1px solid rgba(15,23,42,0.07)",
      boxShadow: "0 12px 36px rgba(15,23,42,0.12)",
      pointerEvents: "none",
      animation: "moversIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.3s both",
      "@keyframes moversIn": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
    }}>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.7 }}>
        Biggest gain, April to July
      </Typography>
      {REG_GAIN_LEGEND.map((r) => (
        <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.4 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: r.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, lineHeight: 1.3, flex: 1 }}>{r.label}</Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: r.color, fontFamily: F }}>{r.districts}</Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, mt: 0.8, fontStyle: "italic", lineHeight: 1.45 }}>
        Districts coloured by which response grew most, shaded by how much. Every district has a riser; "ban entirely" and "no regulation" lead in none.
      </Typography>
    </Box>
  );
}

// Legend for the "who should regulate" map. Carries the question itself as well
// as the key, because the colours encode each district's SECOND choice, which
// no reader would guess from the map on its own. "Both" is in the key greyed
// out with a note, since it wins everywhere and therefore colours nothing.
function WhoRegulatesLegend() {
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 12,
      display: { xs: "none", sm: "block" },
      width: "min(330px, calc(100% - 32px))",
      px: 1.6, py: 1.3, borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(14px)",
      border: "1px solid rgba(15,23,42,0.07)",
      boxShadow: "0 12px 36px rgba(15,23,42,0.12)",
      pointerEvents: "none",
      animation: "moversIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.4s both",
      "@keyframes moversIn": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
    }}>
      {/* The question itself now sits in the card, so the legend is just the
          key. Shorter panel, less map covered, and the wording is stated once. */}
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
        Mapped: each district's second choice
      </Typography>
      {[
        { label: "A single federal regulator", color: "#2a78d6" },
        { label: "Individual states",          color: "#eb6834" },
        { label: "Don't know",                 color: "#78716c" },
      ].map((r) => (
        <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.4 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: r.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, lineHeight: 1.3 }}>{r.label}</Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, mt: 0.8, fontStyle: "italic", lineHeight: 1.45 }}>
        "Both, sharing responsibility" is first everywhere, so it colours no district here.
      </Typography>
    </Box>
  );
}

// The movers list as a collapsible panel over the map. Unlike the Texas movers
// overlay this one is INTERACTIVE, so it takes pointer events: clicking a
// district outlines and zooms to it, clicking again zooms back out.
//
// Collapsible because the grid is ten rows across two columns and permanently
// parking that over the map would hide most of the country. It opens expanded,
// so the affordance is visible, and the header collapses it to a pill.
//
// Bottom-left: the frame slide's camera is the full lower 48, where that corner
// is the Pacific off Baja. Desktop only; on xs the same grid stays in the card.
function FrameMoversOverlay({ movers, optLabel, highlightId, onHighlight }) {
  const [open, setOpen] = useState(true);
  return (
    <Box sx={{
      position: "absolute", bottom: 16, left: 16, zIndex: 12,
      display: { xs: "none", sm: "block" },
      width: open ? "min(360px, calc(100% - 32px))" : "auto",
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(14px)",
      border: "1px solid rgba(15,23,42,0.07)",
      boxShadow: "0 12px 36px rgba(15,23,42,0.12)",
      overflow: "hidden", pointerEvents: "auto",
      transition: "width 0.25s ease",
      animation: "moversIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.4s both",
      "@keyframes moversIn": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
    }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, cursor: "pointer", userSelect: "none", borderBottom: open ? "1px solid rgba(15,23,42,0.07)" : "none", "&:hover": { bgcolor: `${TEAL}08` } }}
      >
        <Typography sx={{ fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, whiteSpace: "nowrap" }}>
          Biggest risers
        </Typography>
        <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: TEAL, fontFamily: F, whiteSpace: "nowrap" }}>
          {optLabel}
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s ease" }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={MUTED_LIGHT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </Box>
      </Box>
      {open && (
        <Box sx={{ px: 1.5, pt: 1.2, pb: 1.4 }}>
          <FrameMoversGrid movers={movers} highlightId={highlightId} onHighlight={onHighlight} />
          {highlightId && (
            <Box onClick={() => onHighlight(null)} sx={{ mt: 1, display: "inline-flex", alignItems: "center", gap: 0.6, cursor: "pointer", px: 1.1, py: 0.45, borderRadius: "999px", border: `1px solid ${TEAL}44`, bgcolor: `${TEAL}08`, "&:hover": { bgcolor: `${TEAL}14` } }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: TEAL, fontFamily: F }}>Clear outline and zoom out</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// The movers grid, shared by the card (mobile) and the map dropdown (desktop),
// so the two can never disagree about which districts moved.
function FrameMoversGrid({ movers, highlightId, onHighlight }) {
  // Risers only. Both tracked responses went UP nationally between April and
  // July ("different" +1.8, "same" +2.3), so the districts that fell are moving
  // against the country and were noise next to the story the slide tells. If a
  // falling response is ever added to FRAME_OPTS this needs a direction flag
  // rather than a hard-coded movers.up.
  return (
    <Box>
      {movers.up.map((d) => {
        const on = highlightId === d.id;
        const delta = Math.round((d.w4 - d.w3) * 10) / 10;
        return (
          <Box
            key={d.id}
            onClick={() => onHighlight(on ? null : d.id)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer",
              px: 0.7, py: 0.45, mb: 0.25, borderRadius: "7px",
              bgcolor: on ? `${TEAL}14` : "transparent",
              border: `1px solid ${on ? TEAL + "44" : "transparent"}`,
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: on ? `${TEAL}18` : "rgba(15,23,42,0.03)" },
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 800, color: INK, fontFamily: F, minWidth: 42, flexShrink: 0 }}>{d.name}</Typography>
            <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.region}</Typography>
            <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, flexShrink: 0 }}>{d.w3.toFixed(1)}→{d.w4.toFixed(1)}</Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#059669", fontFamily: F, minWidth: 34, textAlign: "right", flexShrink: 0 }}>
              +{delta.toFixed(1)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// Teal in-card instruction box, the same component the self-driving and NJI
// reels use. This replaced a dark pill that floated over the map: the pill sat
// on top of the districts it was talking about, said the same thing on every
// slide, and could not name the controls, which are in the card rather than on
// the map.
function ClickHint({ children, mobileChildren }) {
  return (
    <Box sx={{ mt: 1.3, px: 1.5, py: 1, borderRadius: "10px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}20`, display: "flex", alignItems: "flex-start", gap: 1.1 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
      </svg>
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: mobileChildren ? { xs: "none", sm: "block" } : "block" }}>
        {children}
      </Typography>
      {mobileChildren && (
        <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: { xs: "block", sm: "none" } }}>
          {mobileChildren}
        </Typography>
      )}
    </Box>
  );
}

const HintStrong = ({ children }) => <strong style={{ color: TEAL }}>{children}</strong>;

// ─── Cards ────────────────────────────────────────────────────────────────────
// Each card carries ONE idea: a hero number and the minimum that supports it.
// Earlier versions stacked four or five blocks per slide and nothing led.

// Title slide, carried over from the first prediction markets reel. This one is
// a left column against a LIVE map rather than a centred hero over a blurred
// one: the whole point of the slide is that the two toggles work, which a
// blurred backdrop would hide.
function IntroCard({ step, onScrollNext }) {
  return (
    <>
      <Box sx={fu(0.05)}><StackLogo /></Box>
      <Box sx={fu(0.12)}>
        <Eyebrow>District Profiles</Eyebrow>
        <IntroHeadline>{step.headline}</IntroHeadline>
      </Box>
      <Box sx={fu(0.22)}>
        <BodyCopy>{step.copy}</BodyCopy>
      </Box>
      <Box sx={fu(0.32)}>
        <Box sx={{ px: 1.4, py: 1.1, borderRadius: "12px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}1a`, mt: 2 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, mb: 0.3 }}>
            MRP methodology
          </Typography>
          <Typography sx={{ fontSize: "12px", color: MUTED, lineHeight: 1.65, fontFamily: F }}>
            We then apply MRP modelling. First, multilevel regression estimates the relationship between respondents' demographic characteristics and their survey answers. Poststratification then applies those relationships to the known demographic composition of each geography, producing local estimates for congressional districts, counties, states, and state legislative districts.
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK, lineHeight: 1.65, fontFamily: F, mt: 0.75 }}>
            The map shows approval of the current administration's approach to the economy.
          </Typography>
        </Box>
        <ClickHint
          mobileChildren={<><HintStrong>Tap Wave 1 to 4</HintStrong> at the top of the map to move through the tracker, and <HintStrong>tap State, CD or Media market</HintStrong> at the bottom to change geography.</>}
        >
          <HintStrong>Click Wave 1 to 4</HintStrong> in the top right of the map to move through the tracker, <HintStrong>click State, CD or Media market</HintStrong> in the bottom left to change geography, and <HintStrong>hover any area</HintStrong> for its local numbers.
        </ClickHint>
        <Box sx={{ mt: 1.5, px: 1.4, py: 1.25, borderRadius: "12px", border: `1px solid ${BORDER}`, bgcolor: PAPER }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.5 }}>
            Case study
          </Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.45, mb: 0.4 }}>
            Prediction market regulation
          </Typography>
          <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.65, mb: 1.5 }}>
            The rest of the reel uses prediction market regulation as a live example: how one regulatory question moves between April and July 2026, and how it varies by congressional district, demographic group, and state. Both waves are modelled on the same district boundaries, so the movement is a like-for-like comparison.
          </Typography>
          <Box
            onClick={onScrollNext}
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none", "&:hover .scroll-label": { color: MUTED }, "&:hover .scroll-arrow": { borderColor: `${TEAL}88` } }}
          >
            <Box className="scroll-arrow" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: `1.5px solid ${TEAL}44`, transition: "border-color 0.2s ease", animation: "scrollBounce 1.6s ease-in-out 1.5s infinite", "@keyframes scrollBounce": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(5px)" } } }}>
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={TEAL} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Box>
            <Typography className="scroll-label" sx={{ fontSize: "11px", fontWeight: 600, color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.05em", textTransform: "uppercase", transition: "color 0.2s ease" }}>Scroll or click to explore</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// Slide 2: the five regulatory answers as tabs, each redrawing the map. The
// shares are hard-coded wave 4 nationals rather than fetched, matching every
// other card in this reel.
function SurveyQuestion({ question, options = [] }) {
  return (
    <Box sx={{ mt: 1.3, px: 1.5, py: 1.1, borderRadius: "10px", bgcolor: "rgba(15,23,42,0.03)", border: "1px solid rgba(15,23,42,0.08)" }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.55 }}>
        Survey question
      </Typography>
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.45, mb: 0.75 }}>
        {question}
      </Typography>
      {options.map((opt) => (
        <Box key={opt} sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.22 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: MUTED_LIGHT, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{opt}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function RegulationAppetiteCard({ step, isActive, activeFilter, onFilterChange, view, onViewChange }) {
  const filter = REG_FILTERS.find((f) => f.key === activeFilter) || REG_FILTERS[0];

  return (
    <>
      <StackLogo />
      <Eyebrow>The Regulatory Landscape</Eyebrow>
      <Headline>{step.headline}</Headline>
      {step.copy && <BodyCopy>{step.copy}</BodyCopy>}

      <SurveyQuestion
        question="Which of the following comes closest to your view on how prediction markets should be regulated?"
        options={[
          "Regulated the same as gambling",
          "Regulated differently from gambling",
          "No regulation",
          "Banned entirely",
          "Not sure",
        ]}
      />

      {/* Two things the map can show: the level in July, or which response grew
          most since April. The response tabs drive the map in "share" and the
          card only in "biggest gain", because the gain map is categorical
          across all five at once. The hint says so. */}
      <Box sx={{ mt: 1.3, display: "flex", gap: 0.7 }}>
        {[
          { key: "share", label: "Share in July 2026" },
          { key: "gain",  label: "Biggest gain since April" },
        ].map((v) => {
          const on = view === v.key;
          return (
            <Box key={v.key} onClick={() => onViewChange(v.key)} sx={{
              px: 1.4, py: 0.7, borderRadius: "999px", cursor: "pointer", userSelect: "none",
              bgcolor: on ? TEAL : "transparent",
              border: `1px solid ${on ? TEAL : BORDER}`,
              transition: "all 0.18s ease",
              "&:hover": { bgcolor: on ? TEAL_DARK : `${TEAL}0c` },
            }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: on ? "#fff" : MUTED, fontFamily: F, lineHeight: 1, whiteSpace: "nowrap" }}>
                {v.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <ClickHint
        mobileChildren={view === "gain"
          ? <><HintStrong>The map now shows which response grew most</HintStrong> in each district between April and July. The tabs below still move the figures.</>
          : <><HintStrong>Tap each response below</HintStrong> to map where that view is concentrated. Wave 4, July 2026.</>}
      >
        {view === "gain"
          ? <><HintStrong>The map now shows which response grew most</HintStrong> in each district between April and July, shaded by how much. The tabs below still move the figures, and <HintStrong>hover any district</HintStrong> for its local numbers.</>
          : <><HintStrong>Click each response below</HintStrong> to map where that view is concentrated, and <HintStrong>hover any district</HintStrong> for its local numbers. Wave 4, July 2026.</>}
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {REG_FILTERS.map((f) => {
            const active = f.key === activeFilter;
            return (
              <Box key={f.key} onClick={() => onFilterChange(f.key)} sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 0.5, px: 0.5, py: 1, cursor: "pointer",
                bgcolor: active ? `${f.color}10` : "transparent",
                borderBottom: active ? `2.5px solid ${f.color}` : "2.5px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": { bgcolor: `${f.color}0c` },
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: active ? f.color : `${f.color}70`, transition: "background 0.15s ease", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "10px", fontWeight: active ? 700 : 500, color: active ? f.color : MUTED_LIGHT, fontFamily: F, lineHeight: 1.2, textAlign: "center", transition: "all 0.15s ease" }}>
                  {f.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box key={activeFilter} sx={{ p: 2.5, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1.5 }}>
            <Typography sx={{ fontSize: "56px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.06em", color: filter.color, flexShrink: 0 }}>
              <CountUp value={`${filter.pct}%`} isActive={isActive} />
            </Typography>
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.2 }}>{filter.label}</Typography>
              <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mt: 0.3, lineHeight: 1.3 }}>{filter.sublabel}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.7, borderTop: `1px solid ${BORDER}`, pt: 1.25 }}>
            {filter.cluster}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

// One distribution strip: the 436 district estimates for a group, as a box and
// whiskers on the shared axis. Whiskers are min to max, the box is the middle
// half, the solid tick is July's median and the hollow one April's.
function DistStrip({ name, stat, axis, color, selected, onClick, showAxis, selectedTone }) {
  const tone = selectedTone || color;
  const [lo, hi] = axis;
  const pos = (v) => ((v - lo) / (hi - lo)) * 100;
  const [dmin, q1, med, q3, dmax] = stat.jul;
  const shift = Math.round((med - stat.aprMed) * 10) / 10;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25, cursor: onClick ? "pointer" : "default",
        px: 0.85, py: 0.75, borderRadius: "9px", mb: 0.4,
        bgcolor: selected ? `${tone}16` : "transparent",
        border: `1px solid ${selected ? tone + "55" : "transparent"}`,
        transition: "all 0.15s ease",
        ...(onClick ? { "&:hover": { bgcolor: selected ? `${tone}1e` : `${tone}0e` } } : {}),
      }}
    >
      <Typography sx={{ fontSize: "11.5px", fontWeight: selected ? 800 : 600, color: selected ? INK : MUTED, fontFamily: F, minWidth: 96, lineHeight: 1.2 }}>
        {name}
      </Typography>

      <Box sx={{ flex: 1, position: "relative", height: 24 }}>
        {/* Baseline track, so an empty stretch of axis still reads as axis. */}
        <Box sx={{ position: "absolute", left: 0, right: 0, top: 11, height: 2, borderRadius: 1, bgcolor: "rgba(15,23,42,0.05)" }} />
        {/* min to max */}
        <Box sx={{ position: "absolute", top: 11, height: 2, left: `${pos(dmin)}%`, width: `${pos(dmax) - pos(dmin)}%`, bgcolor: color, opacity: 0.4, borderRadius: 1 }} />
        {/* middle half */}
        <Box sx={{ position: "absolute", top: 6, height: 12, left: `${pos(q1)}%`, width: `${pos(q3) - pos(q1)}%`, bgcolor: color, opacity: 0.5, borderRadius: "3px" }} />
        {/* April median, hollow */}
        <Box sx={{ position: "absolute", top: 4, height: 16, width: 2, left: `${pos(stat.aprMed)}%`, bgcolor: "#fff", border: `1px solid ${INK}55`, borderRadius: 1 }} />
        {/* July median, solid */}
        <Box sx={{ position: "absolute", top: 2, height: 20, width: 3, left: `${pos(med)}%`, bgcolor: color, borderRadius: 1, boxShadow: "0 0 0 1.5px rgba(255,255,255,0.9)" }} />
        {showAxis && [lo, Math.round((lo + hi) / 2), hi].map((v) => (
          <Typography key={v} sx={{ position: "absolute", top: 24, left: `${pos(v)}%`, transform: "translateX(-50%)", fontSize: "8.5px", color: MUTED_LIGHT, fontFamily: F }}>
            {v}%
          </Typography>
        ))}
      </Box>

      <Typography sx={{ fontSize: "12px", fontWeight: 700, color, fontFamily: F, minWidth: 42, textAlign: "right" }}>{med.toFixed(1)}%</Typography>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, fontFamily: F, minWidth: 38, textAlign: "right", color: shift > 0 ? "#059669" : shift < 0 ? "#e11d48" : MUTED_LIGHT }}>
        {shift > 0 ? "+" : ""}{shift.toFixed(1)}
      </Typography>
    </Box>
  );
}

// Slide 2. Small multiples of the district distribution, one strip per group on
// a shared axis. Position compares the groups, the box shows the spread inside
// each. Selecting a strip also re-maps the country for that group.
function FrameCard({ step, optKey, onOptChange, cutIdx, onCutChange, rowIdx, onRowChange, highlightId, onHighlight }) {
  const cut = FRAME_CUTS[cutIdx];
  const movers = FRAME_MOVERS[optKey];
  const opt = FRAME_OPTS.find((o) => o.key === optKey) || FRAME_OPTS[0];
  const dist = FRAME_DIST[optKey];
  const axis = dist.axis;

  return (
    <>
      <StackLogo />
      <Eyebrow>Gambling or not · April to July 2026</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <ClickHint
        mobileChildren={<><HintStrong>Tap a response</HintStrong> to change what is mapped and <HintStrong>tap a group</HintStrong> to filter to it. With <HintStrong>All adults</HintStrong> selected, <HintStrong>tap a district</HintStrong> in the risers list to outline it on the map.</>}
      >
        <HintStrong>Click a response</HintStrong> to change what is mapped and <HintStrong>click a group</HintStrong> to filter the map to it. With <HintStrong>All adults</HintStrong> selected, the biggest risers appear over the map: <HintStrong>click a district</HintStrong> to outline and zoom to it, and click it again to zoom back out.
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {FRAME_CUTS.map((c, i) => {
            const on = i === cutIdx;
            return (
              <Box key={c.key} onClick={() => onCutChange(i)} sx={{ flex: 1, py: 1, cursor: "pointer", textAlign: "center", bgcolor: on ? `${TEAL}10` : "transparent", borderBottom: on ? `2.5px solid ${TEAL}` : "2.5px solid transparent", transition: "all 0.15s ease", "&:hover": { bgcolor: `${TEAL}0c` } }}>
                <Typography sx={{ fontSize: "11px", fontWeight: on ? 700 : 500, color: on ? TEAL : MUTED_LIGHT, fontFamily: F }}>{c.label}</Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ px: 2.25, pt: 1.6, pb: 0.6, display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>
          {FRAME_OPTS.map((o) => {
            const on = o.key === optKey;
            return (
              <Box key={o.key} onClick={() => onOptChange(o.key)} sx={{ px: 1.3, py: 0.55, borderRadius: "999px", cursor: "pointer", bgcolor: on ? o.color : "transparent", border: `1.5px solid ${on ? o.color : o.color + "55"}`, color: on ? "#fff" : MUTED, fontSize: "11px", fontWeight: 700, fontFamily: F, transition: "all 0.18s ease", userSelect: "none", "&:hover": { bgcolor: on ? o.color : `${o.color}18` } }}>
                {o.label}
              </Box>
            );
          })}
        </Box>

        <Box key={`${cut.key}-${optKey}`} sx={{ px: 2.25, pb: 2.25, pt: 0.8, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <CardLabel>{opt.label} · % of adults by district, shared axis</CardLabel>

          {/* National reference, not selectable: it is the line the groups are
              read against rather than a group you can map on its own. */}
          <DistStrip
            name="All adults"
            stat={dist["All adults"]}
            axis={axis}
            color={MUTED_LIGHT}
            selectedTone={TEAL}
            selected={rowIdx === -1}
            onClick={() => onRowChange(-1)}
          />
          <Box sx={{ height: "1px", bgcolor: BORDER, my: 0.7 }} />

          {cut.rows.map((name, i) => (
            <DistStrip
              key={name}
              name={name}
              stat={dist[name]}
              axis={axis}
              color={opt.color}
              selected={i === rowIdx}
              onClick={() => onRowChange(i)}
              showAxis={i === cut.rows.length - 1}
            />
          ))}

          <Box sx={{ mt: 2.2, display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 22, height: 10, borderRadius: "3px", bgcolor: opt.color, opacity: 0.5 }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>Middle half of districts</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 3, height: 14, borderRadius: 1, bgcolor: opt.color }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>July median</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 2, height: 14, borderRadius: 1, bgcolor: "#fff", border: `1px solid ${INK}55` }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>April median</Typography>
            </Box>
            <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>Thin line spans lowest to highest district</Typography>
          </Box>

          <Note>{cut.note}</Note>

          <Box sx={{ mt: 1.25, px: 1.5, py: 1.1, borderRadius: "12px", bgcolor: `${ACCENT}0a`, border: `1px solid ${ACCENT}30` }}>
            <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: ACCENT, fontFamily: F, mb: 0.45 }}>
              The crossover
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, lineHeight: 1.65 }}>
              Cross 2024 vote with age and one group has already flipped. Among <strong style={{ color: INK }}>Trump voters under 35</strong>, "different from gambling" went 23.4% to 33.4% between April and July and overtook "same as gambling" at 31.9%. That is the largest move of any group measured, and the only group anywhere that now leads on "different". Neither cut above shows it, because cutting by age or vote alone averages it away.
            </Typography>
          </Box>

          {/* Mobile only: on desktop this same list is a dropdown over the map.
              Shown for the national baseline only, matching the overlay. */}
          {rowIdx === -1 && (
          <Box sx={{ display: { xs: "block", sm: "none" }, mt: 1.6, pt: 1.4, borderTop: `1px solid ${BORDER}` }}>
            <CardLabel>Biggest risers · tap to outline on the map</CardLabel>
            <FrameMoversGrid movers={movers} highlightId={highlightId} onHighlight={onHighlight} />
            {highlightId && (
              <Box onClick={() => onHighlight(null)} sx={{ mt: 0.8, display: "inline-flex", alignItems: "center", gap: 0.6, cursor: "pointer", px: 1.1, py: 0.45, borderRadius: "999px", border: `1px solid ${TEAL}44`, bgcolor: `${TEAL}08`, "&:hover": { bgcolor: `${TEAL}14` } }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: TEAL, fontFamily: F }}>Clear outline</Typography>
              </Box>
            )}
          </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

// Slide 4. One idea: first choice is unanimous, second choice splits.
function WhoRegulatesCard({ step, isActive }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Federal or state · July 2026</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      {/* Question wording only. The five answers are enumerated with their
          shares in the bar chart below, so listing them here too would repeat
          the same five labels on one card. */}
      <SurveyQuestion question="Who do you think should be mainly responsible for regulating prediction markets?" />

      <ClickHint
        mobileChildren={<><HintStrong>Tap the expand button on the map</HintStrong> to see each district's second choice up close.</>}
      >
        <HintStrong>Hover any district on the map</HintStrong> to see its full breakdown across all five answers, not just the second choice the colour shows.
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ p: 2.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.6 }}>
            <Typography sx={{ fontSize: "46px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.05em", color: "#4a3aa7" }}>
              <CountUp value="35.7%" isActive={isActive} />
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: MUTED, fontFamily: F, lineHeight: 1.6, flex: 1 }}>
              say federal and state should <strong style={{ color: INK }}>share responsibility</strong>. 
            </Typography>
          </Box>

          <CardLabel>Who should regulate prediction markets</CardLabel>
          {WHO_REGULATES.map((r) => (
            <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.65 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: r.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, minWidth: 190, lineHeight: 1.3 }}>{r.label}</Typography>
              <Box sx={{ flex: 1, height: 6, borderRadius: "3px", bgcolor: `${r.color}22`, overflow: "hidden" }}>
                {/* Scaled to the largest answer rather than to 100, so the gaps
                    between five shares that all sit under 36% stay readable. */}
                <Box sx={{ width: `${(r.pct / 35.7) * 100}%`, height: "100%", bgcolor: r.color, borderRadius: "3px" }} />
              </Box>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: r.color, fontFamily: F, minWidth: 40, textAlign: "right" }}>{r.pct}%</Typography>
            </Box>
          ))}

          <Box sx={{ mt: 1.6, pt: 1.4, borderTop: `1px solid ${BORDER}` }}>
            <CardLabel>Second choice, by group</CardLabel>
            {/* Two columns: five stacked rows overflowed the card. "All adults"
                stays full width above the grid because it is the reference the
                four groups are read against, not one of them, and it also makes
                the remaining four fill a 2x2 with no ragged gap. */}
            {(() => {
              const [ref, ...rest] = SECOND_BY_GROUP;
              const row = (g, full) => (
                <Box key={g.label} sx={{ display: "flex", alignItems: "center", gap: 0.85, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "11.5px", color: full ? INK : MUTED, fontWeight: full ? 700 : 400, fontFamily: F, minWidth: 66, lineHeight: 1.3, flexShrink: 0 }}>
                    {g.label}
                  </Typography>
                  <Box sx={{ px: 0.8, py: 0.28, borderRadius: "6px", bgcolor: `${g.color}18`, border: `1px solid ${g.color}3a`, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: g.color, fontFamily: F, whiteSpace: "nowrap" }}>
                      {g.second}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, ml: "auto", flexShrink: 0 }}>{g.pct}%</Typography>
                </Box>
              );
              return (
                <>
                  <Box sx={{ pb: 0.7, mb: 0.7, borderBottom: `1px solid ${BORDER}` }}>{row(ref, true)}</Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 2, rowGap: 0.7 }}>
                    {rest.map((g) => row(g, false))}
                  </Box>
                </>
              );
            })()}
            <Note>
              The generation most likely to actually use a prediction market is the one least attached to the federal-preemption argument. Under-35s and non-graduates put individual states second; graduates and over-65s put a federal regulator second.
            </Note>
          </Box>
        </Box>
      </Box>
    </>
  );
}


// Slide 6. Texas: three stats, no chart. The numbers are the point.
function TexasCard({ step }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Texas · 2027 session</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%" }}>
        {TEXAS_STATS.map((t, i) => (
          <Box key={t.label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, p: 1.75, mb: 0.85, borderRadius: "14px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${t.color}`, ...fuStagger(0.1, i) }}>
            <Box sx={{ minWidth: 78 }}>
              <Typography sx={{ fontSize: "26px", fontWeight: 800, color: t.color, fontFamily: F, lineHeight: 1, letterSpacing: "-0.04em" }}>{t.stat}</Typography>
              <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.35, mt: 0.45 }}>{t.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.68, flex: 1 }}>{t.body}</Typography>
          </Box>
        ))}
        <Box sx={{ mt: 0.4, p: 1.75, borderRadius: "14px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}` }}>
          <TexasMoversList />
        </Box>

        <Note>
          Texas districts also moved further than any other large state: an average rise of 2.3 points on "different from gambling" against 0.7 in California, and three of the five biggest-moving seats in the country are Texan.
        </Note>
      </Box>
    </>
  );
}

// Closing hero. Three takeaways, three next steps, and the call to action in
// the same panel, so the reel ends on one slide rather than two.
function SummaryCard({ step, authState, onExit }) {
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%" }}>
        <StackLogo />
        <Eyebrow>Next Steps</Eyebrow>
        <Typography sx={{ fontSize: { xs: "28px", sm: "34px" }, fontWeight: 500, color: INK, fontFamily: F, lineHeight: 1.1, letterSpacing: "-0.03em", mb: 2 }}>
          {step.headline}
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 1.1, width: "100%" }}>
        {TAKEAWAYS.map((t, i) => (
          <Box key={t.title} sx={{ px: 1.7, py: 1.4, borderRadius: "16px", border: `1px solid ${BORDER}`, bgcolor: "rgba(255,255,255,0.9)", textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, mb: 0.4 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, color: TEAL, fontFamily: F }}>{`0${i + 1}`}</Typography>
              <Typography sx={{ fontSize: { xs: "14.5px", sm: "13px" }, fontWeight: 700, color: INK, fontFamily: F }}>{t.title}</Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ mt: 2.2, mb: 1, alignSelf: "flex-start", fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        What to ask next
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.1, width: "100%" }}>
        {NEXT_STEPS.map((t) => (
          <Box key={t.title} sx={{ px: 1.6, py: 1.3, borderRadius: "16px", border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}06`, textAlign: "left" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 700, color: INK, fontFamily: F, mb: 0.4, lineHeight: 1.3 }}>{t.title}</Typography>
            <Typography sx={{ fontSize: { xs: "13px", sm: "11.5px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>

      {/* Signed-in visitors get the pill straight through to the app; logged-out
          ones get the demo form, because this reel is gated. */}
      {authState === "loggedIn" ? (
        <Box
          onClick={onExit}
          sx={{ mt: 2.2, alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 0.9, cursor: "pointer", px: 2.2, py: 1, borderRadius: "999px", border: `1.5px solid ${TEAL}`, bgcolor: PAPER, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}0d` } }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1 }}>
            Explore the data in District Profiles
          </Typography>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Box>
      ) : (
        <Box sx={{ mt: 2.2, width: "100%", maxWidth: 380, alignSelf: "center" }}>
          <DemoSignupOverlay embedded />
        </Box>
      )}

      <Typography sx={{ mt: 1.25, fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.5, alignSelf: "center", textAlign: "center" }}>
        MRP estimates, n over 8,000 per wave, April and July 2026. Local and demographic figures are modelled, not measured.
      </Typography>
    </>
  );
}

function DemoSignupOverlay({ embedded = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/user/demo-signup/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setSuccess(data?.verify_url ? `Dev link: ${data.verify_url}` : true);
    } catch (err) {
      setError(err.message || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <Box sx={{ width: embedded ? "100%" : "min(420px, 90%)", bgcolor: "#fff", borderRadius: embedded ? "0" : "16px", p: embedded ? 0 : 3.5 }}>
      {success ? (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 1 }}>Check your email</Typography>
          <Typography sx={{ fontSize: "14px", color: MUTED, fontFamily: F, lineHeight: 1.6 }}>
            {typeof success === "string" ? success : "Demo account created. Check your email to verify and activate access."}
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 0.5 }}>Get demo access</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Password" type="password" required value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            {error && <Typography sx={{ fontSize: "12px", color: "#e11d48", fontFamily: F }}>{error}</Typography>}
            <Button type="submit" variant="contained" disableElevation disabled={submitting || !email || !password}
              sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "10px", py: 1.1, boxShadow: `0 4px 20px ${TEAL_GLOW}`, "&:hover": { bgcolor: TEAL_DARK }, "&:disabled": { bgcolor: `${TEAL}55`, color: "#fff" } }}>
              {submitting ? "Creating account..." : "Get demo access"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  if (embedded) return content;
  return (
    <Box sx={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}>
      {content}
    </Box>
  );
}

const AppPanelWrapper = React.forwardRef(function AppPanelWrapper({ interactive = false }, ref) {
  const noopNavigate = useCallback(() => {}, []);
  return (
    <MainPage
      ref={ref}
      slug="gamble"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
      disableMapInteraction={!interactive}
      geoPreloadOrder={["media_market"]}
    />
  );
});

// ─── NarrativeCard ────────────────────────────────────────────────────────────
function NarrativeCard(props) {
  const { step, index, total, isActive, cardRef, authState, onScrollNext } = props;
  const navigate = useNavigate();
  const nextStep = STEPS[index + 1];

  // The summary is the reel's one overlay hero: the map runs full-width and
  // blurred behind a centred frosted panel. The intro used to be one too, but
  // it now needs a legible map for its wave and geography toggles, so it is a
  // normal left-column slide.
  const isSummaryHero = step.name === "summary";
  const isOverlayHero = !!step.hero;
  const heroMaxWidth  = "min(980px, 94vw)";

  const cardSx = {
    height: { xs: "auto", sm: "100vh" },
    minHeight: { xs: "80vh", sm: "auto" },
    flexShrink: 0,
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    pt: { xs: 3.5, sm: isOverlayHero ? 0 : 5, md: isOverlayHero ? 0 : 6 },
    pb: { xs: 5, sm: 5, md: 6 },
    px: { xs: 2.5, sm: 3.5, md: 4.5 },
    "@media (min-width: 600px) and (max-height: 900px)": {
      pt: isOverlayHero ? 0 : 3,
      pb: 3,
    },
    position: "relative",
    overflowY: { xs: "visible", sm: isOverlayHero ? "hidden" : "auto" },
    borderLeft: "2px solid",
    borderColor: isOverlayHero ? "transparent" : (isActive ? `${TEAL}28` : "transparent"),
    opacity: isOverlayHero ? 1 : (isActive ? 1 : 0.38),
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isOverlayHero ? "transparent" : (isActive ? `linear-gradient(90deg, ${TEAL}05 0%, transparent 20%)` : "transparent"),
  };

  const inner = (() => {
    switch (step.name) {
      case "intro":         return <IntroCard step={step} onScrollNext={onScrollNext} />;
      case "regulation-appetite": return <RegulationAppetiteCard step={step} isActive={isActive} activeFilter={props.regFilter} onFilterChange={props.onRegFilterChange} view={props.regView} onViewChange={props.onRegViewChange} />;
      case "frame":         return <FrameCard step={step} optKey={props.optKey} onOptChange={props.onOptChange} cutIdx={props.cutIdx} onCutChange={props.onCutChange} rowIdx={props.rowIdx} onRowChange={props.onRowChange} highlightId={props.highlightId} onHighlight={props.onHighlight} />;
      case "who-regulates": return <WhoRegulatesCard step={step} isActive={isActive} />;
      case "texas":         return <TexasCard step={step} />;
      case "summary":       return <SummaryCard step={step} authState={authState} onExit={() => exitHighlights(navigate)} />;
      default: return <><Headline>{step.headline}</Headline>{step.copy && <BodyCopy>{step.copy}</BodyCopy>}</>;
    }
  })();

  if (isOverlayHero) {
    return (
      <Box ref={cardRef} data-index={index} sx={cardSx}>
        {isActive && (
          <Box sx={{ display: "flex", position: { xs: "relative", sm: "fixed" }, inset: { xs: "auto", sm: 0 }, zIndex: 20, height: { xs: "auto", sm: "100vh" }, alignItems: "center", justifyContent: "center", px: { xs: 0, sm: 3, md: 5 }, py: { xs: 0, sm: 3 }, pointerEvents: "none" }}>
            <Panel
              data-hero-scroll="true"
              sx={{ maxWidth: { xs: "100%", sm: heroMaxWidth }, mx: "auto", maxHeight: { xs: "none", sm: "calc(100vh - 40px)" }, overflowY: { xs: "visible", sm: "auto" }, borderRadius: "22px", p: { xs: 2.5, sm: 3.5, md: 3.5 }, textAlign: "left", pointerEvents: "auto", bgcolor: "rgba(255,255,255,0.85)", backgroundImage: "none", backdropFilter: "blur(20px)", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 26px 64px rgba(15,23,42,0.08)", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", animation: "cardEnter 0.6s cubic-bezier(0.22,1,0.36,1) both", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                {inner}
                {!isSummaryHero && <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} />}
              </Box>
            </Panel>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      <Box key={isActive ? "active" : "idle"} sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", maxWidth: 780, animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
        <Box sx={{ width: "100%", flex: { xs: "0 0 auto", sm: 1 }, display: "flex", flexDirection: "column", justifyContent: { xs: "flex-start", sm: "safe center" } }}>
          {inner}
        </Box>
        <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PredictionMarketsWave4Page() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [authState, setAuthState] = useState("guest");
  const [cutIdx, setCutIdx] = useState(0);
  const [optKey, setOptKey] = useState("diff");
  // area_id of the district outlined on the map, or null.
  const [highlightId, setHighlightId] = useState(null);
  // Title slide controls. Wave 4 and CD are the defaults, so the slide opens on
  // the latest data at the geography the rest of the reel uses.
  const [activeWave, setActiveWave] = useState(4);
  const [introGeo, setIntroGeo] = useState("cd");
  // Slide 2's selected regulatory answer.
  const [regFilter, setRegFilter] = useState(REG_FILTERS[0].key);
  // "share" or "gain" on slide 2. Compare-waves is MainPage state rather than
  // part of the setHighlightsState contract, so the handler writes it straight
  // onto the instance, the same way chatHighlightAreas is written.
  const [regView, setRegView] = useState("share");
  const [rowIdx, setRowIdx] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [labelExpanded, setLabelExpanded] = useState(false);

  const cardRefs = useRef([]);
  const mainRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const visibleSet = useRef(new Set());
  const activeIndexRef = useRef(0);
  const mapPanelRef = useRef(null);

  // The two overlay heroes take the map full-width and blur it as a backdrop.
  const isHeroStep = !!STEPS[activeIndex]?.hero;
  const activeStep = STEPS[activeIndex];

  useEffect(() => {
    fetch("/api/user-info/")
      .then((r) => r.json())
      .then((d) => setAuthState(d?.email ? "loggedIn" : "loggedOut"))
      .catch(() => setAuthState("guest"));
  }, []);

  // Ported from the self-driving reel. Two things the previous version got
  // wrong: it advanced a slide on every wheel event (so one trackpad flick
  // skipped several), and it had no notion of a hero panel that scrolls
  // internally, so the centred intro and summary cards could not be scrolled at
  // all when their content overflowed.
  const targetIndexRef = useRef(null);
  const unlockTimerRef = useRef(null);
  useEffect(() => () => { if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current); }, []);

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    targetIndexRef.current = i;
    activeIndexRef.current = i;
    visibleSet.current = new Set([i]);
    setActiveIndex(i);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = window.setTimeout(() => {
      unlockTimerRef.current = null;
      if (container) container._scrollLocked = false;
      targetIndexRef.current = null;
      // Smooth scrolling can land a pixel or two off and leave the snap
      // half-engaged; correct it silently once the animation has settled.
      const el = cardRefs.current[i];
      if (!el) return;
      if (window.innerWidth >= 600 && container) {
        const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (Math.abs(offset) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
      }
    }, 1200);
  }, []);

  const getNearestCardIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !cardRefs.current.length) return null;
    const containerTop = container.getBoundingClientRect().top;
    let nearestIdx = null;
    let nearestDistance = Infinity;
    cardRefs.current.forEach((card, idx) => {
      if (!card) return;
      const distance = Math.abs(card.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) { nearestDistance = distance; nearestIdx = idx; }
    });
    return nearestIdx;
  }, []);

  // Sliding cooldown: momentum events keep pushing the re-arm back, so one
  // physical flick can never advance two slides.
  const wheelCooldownRef = useRef(false);
  const wheelDeltaRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const wheelNavAtRef = useRef(0);

  const handleWheel = useCallback((e) => {
    if (window.innerWidth < 600) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    // Let the centred hero panel consume the wheel until it bottoms out, so the
    // intro's methodology grid and the summary's takeaways can be read before
    // the reel moves on.
    const heroEl = e.target instanceof Element ? e.target.closest("[data-hero-scroll]") : null;
    if (heroEl instanceof HTMLElement) {
      const canScrollDown = heroEl.scrollTop + heroEl.clientHeight < heroEl.scrollHeight - 2;
      const canScrollUp = heroEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    // Same courtesy for a normal slide whose card is taller than the viewport.
    const cardEl = e.target instanceof Element ? e.target.closest("[data-index]") : null;
    if (cardEl instanceof HTMLElement) {
      const canScrollDown = cardEl.scrollTop + cardEl.clientHeight < cardEl.scrollHeight - 2;
      const canScrollUp = cardEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    e.preventDefault();
    const now = Date.now();
    const gap = now - lastWheelTsRef.current;
    lastWheelTsRef.current = now;
    if (container._scrollLocked) return;
    if (wheelCooldownRef.current) {
      // Release on a 250ms pause in the wheel stream OR a 1.6s hard cap: the
      // pause catches a deliberate second flick straight away, the cap stops
      // long free-spin inertia from locking the reel up.
      const sinceNav = now - wheelNavAtRef.current;
      if (gap < 250 && sinceNav < 1600) return;
      wheelCooldownRef.current = false;
    }

    wheelDeltaRef.current += e.deltaY;
    if (Math.abs(wheelDeltaRef.current) < 70) return;

    const currentIdx = targetIndexRef.current ?? getNearestCardIndex();
    if (currentIdx == null) return;
    const dir = wheelDeltaRef.current > 0 ? 1 : -1;
    const nextIdx = Math.max(0, Math.min(STEPS.length - 1, currentIdx + dir));
    if (nextIdx === currentIdx) { wheelDeltaRef.current = 0; return; }

    wheelDeltaRef.current = 0;
    wheelCooldownRef.current = true;
    wheelNavAtRef.current = Date.now();
    scrollToIndex(nextIdx);
  }, [getNearestCardIndex, scrollToIndex]);

  // React's onWheel is registered PASSIVELY, so preventDefault inside it is
  // silently ignored and native inertia scrolls alongside the programmatic
  // snap, double-advancing. Attach the listeners non-passively ourselves. The
  // column listener also covers the hero panels via bubbling.
  useEffect(() => {
    const els = [scrollContainerRef.current, mapPanelRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", handleWheel, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener("wheel", handleWheel));
  }, [handleWheel]);

  // Per-step resets, so a selection made on one slide never leaks to the next.
  useEffect(() => {
    const n = STEPS[activeIndex]?.name;
    if (n !== "frame") { setCutIdx(0); setRowIdx(0); setOptKey("diff"); }
    // The outline belongs to the frame slide; drop it on the way out.
    if (n !== "frame") setHighlightId(null);
    if (n !== "regulation-appetite") {
      setRegFilter(REG_FILTERS[0].key);
      // Compare mode would otherwise follow the reader onto every later slide.
      setRegView("share");
      mainRef.current?.setState?.({ compareWaves: false });
    }
  }, [activeIndex]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const stepName = STEPS[activeIndex]?.name;
      if (!stepName) return;
      navigateToStep(mainRef.current, stepName, extraMapStateRef.current());
    }, 160);
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let fired = false;
    const poll = setInterval(() => {
      const inst = mainRef.current;
      if (!inst) return;
      const mapPage = inst._mapPageInstance;
      const cdLoaded = mapPage?.state?.geoPreloadStatus?.cd === "loaded";
      if (!fired && inst.state?.mapLoading === false && cdLoaded) {
        const featureCount = mapPage?.state?.geoData?.features?.length ?? 0;
        // Recover a blank map: if the visible geometry is not the CD layer (the
        // mount-time states load can resolve AFTER the cd load and overwrite
        // geoData), reload cd. loadGeo is cache-first so this is cheap when
        // already cached.
        //
        // This used to be gated on _geoCache.has("cd:"), which NEVER matched and
        // so disabled the recovery entirely, leaving the reel stuck on a basemap
        // with no districts. CD cache keys carry the boundary vintage and the
        // mobile marker (MapPage builds "cd::CD120", or "cd::CD120:m" on a
        // phone), so a literal "cd:" lookup is always false. Same fix as
        // NJICreativePage and SelfDrivingPage.
        if (featureCount < 200 && mapPage) {
          mapPage.loadGeo("cd", null, { doFit: false });
        }
        fired = true;
        clearInterval(poll);
        clearTimeout(fallback);
        setMapReady(true);
        const stepName = STEPS[activeIndexRef.current]?.name;
        if (stepName) navigateToStep(inst, stepName, extraMapStateRef.current());
      }
    }, 150);
    const fallback = setTimeout(() => setMapReady(true), 30000);
    return () => { clearInterval(poll); clearTimeout(fallback); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── One source of truth for what the map should show ────────────────────────
  // Every in-card control and the step navigation all route through this, so a
  // slide's map state is always the FULL state rather than whatever dimension
  // the handler that fired happened to know about. The earlier version built
  // this separately in three handlers, which meant arriving on the demographics
  // slide (or flipping its wave) left the map unfiltered while the card showed
  // a group selected. Demographic keys are always written, as null when unused,
  // so a cut can never survive into a slide that does not want it.
  // One source of truth for what the map shows. Every control and the step
  // navigation route through this, so a slide's map state is always the FULL
  // state rather than whatever dimension the handler that fired knew about.
  // Demographic keys are always written, as null when unused, so a cut can
  // never survive into a slide that does not want one.
  const extraMapStateFor = useCallback((step, over = {}) => {
    const cI = over.cutIdx ?? cutIdx;
    const rI = over.rowIdx ?? rowIdx;
    const oK = over.optKey ?? optKey;
    const noDemos = { gender: null, age: null, vote24: null, education: null, ethnicity: null };
    if (step?.name === "intro") {
      // The only slide that is not pinned to wave 4: its whole job is to show
      // the tracker moving. geoLevel overrides step.mapView because callers
      // spread mapView first.
      return { ...noDemos, wave: over.activeWave ?? activeWave, geoLevel: over.introGeo ?? introGeo };
    }
    if (step?.name === "regulation-appetite") {
      return { ...noDemos, wave: 4, preferenceFilter: over.regFilter ?? regFilter };
    }
    if (step?.name === "frame") {
      const cut = FRAME_CUTS[cI];
      const name = cut?.rows[rI];
      const opt = FRAME_OPTS.find((o) => o.key === oK);
      return { ...noDemos, wave: 4, ...(opt ? { preferenceFilter: opt.mapKey } : {}), ...(cut?.filters?.[name] || {}) };
    }
    return noDemos;
  }, [cutIdx, rowIdx, optKey, activeWave, introGeo, regFilter]);

  // The nav effect fires on activeIndex only (re-running it on every control
  // change would re-fly the camera), so it reads the builder through a ref to
  // avoid capturing a stale closure over the selection state.
  const extraMapStateRef = useRef(() => ({}));
  useEffect(() => {
    extraMapStateRef.current = () => extraMapStateFor(STEPS[activeIndexRef.current]);
  }, [extraMapStateFor]);

  // Apply a control change immediately, without touching the camera.
  const applyMapState = useCallback((over) => {
    const step = STEPS[activeIndexRef.current];
    if (!mainRef.current || !step?.mapView) return;
    armCrossfade(getGlMap(mainRef.current));
    mainRef.current.setHighlightsState({ ...step.mapView, ...extraMapStateFor(step, over) });
  }, [extraMapStateFor]);

  const handleCutChange = useCallback((i) => { setCutIdx(i); setRowIdx(0); applyMapState({ cutIdx: i, rowIdx: 0 }); }, [applyMapState]);
  const handleRowChange = useCallback((i) => { setRowIdx(i); applyMapState({ rowIdx: i }); }, [applyMapState]);
  // Return the map to the active step's camera. Used whenever a district
  // highlight is dropped, because the zoom that came with it is one-way.
  const clearZoom = useCallback(() => {
    const step = STEPS[activeIndexRef.current];
    if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    invalidateFlyCache();
  }, []);

  const handleOptChange = useCallback((k) => {
    setOptKey(k);
    // The movers list is per option, so clear an outline that is about to leave it.
    setHighlightId(null);
    mainRef.current?.setState?.({ chatHighlightAreas: [] });
    if (highlightId) clearZoom();
    applyMapState({ optKey: k });
  }, [applyMapState, highlightId, clearZoom]);

  // chatHighlightAreas is the map's outline layer, keyed by area_id. Written
  // straight on the MainPage instance because it is not part of the
  // setHighlightsState contract.
  const handleHighlight = useCallback((id) => {
    setHighlightId(id);
    mainRef.current?.setState?.({ chatHighlightAreas: id ? [id] : [] });
    // The zoom that comes with a highlight moves the camera outside
    // navigateToStep's knowledge, so let the next step change re-fly.
    invalidateFlyCache();
    // Setting a highlight makes MapPage call zoomToChatAreas, so the map frames
    // that district on its own. CLEARING one does not reverse it (MapPage only
    // zooms when the array is non-empty), so the map would stay parked on the
    // last district. Fly back to the step's own framing.
    if (!id) clearZoom();
  }, [clearZoom]);
  const handleWaveChange = useCallback((w) => { setActiveWave(w); applyMapState({ activeWave: w }); }, [applyMapState]);
  const handleIntroGeo = useCallback((g) => { setIntroGeo(g); applyMapState({ introGeo: g }); }, [applyMapState]);
  const handleRegFilter = useCallback((k) => { setRegFilter(k); applyMapState({ regFilter: k }); }, [applyMapState]);
  const handleRegView = useCallback((v) => {
    setRegView(v);
    armCrossfade(getGlMap(mainRef.current));
    // compareKey "__top__" is what makes this the biggest-GAINER map rather
    // than a signed delta ramp; MapPage refetches on compareWaves changing.
    mainRef.current?.setState?.({ compareWaves: v === "gain", compareKey: "__top__" });
  }, []);

  // Mapbox GL does not observe CSS size changes, so every time the panel's box
  // changes the canvas has to be told. Two distinct jobs, deliberately split:
  //
  //   resizeOnly  — reconcile the canvas with its current box. Cheap, safe to
  //                 fire repeatedly mid-transition.
  //   refit       — resize AND re-apply the active step's camera. Re-flies the
  //                 map, so it must fire ONCE at the end of a transition, not
  //                 on every frame of it.
  //
  // The camera is re-applied from the step definition rather than from a
  // captured getBounds(), because the step is viewport-independent and reusing
  // a captured bounds compounds zoom error across successive resizes.
  const withMap = useCallback((fn) => {
    let tries = 0;
    const attempt = () => {
      const map = getGlMap(mainRef.current);
      // The MainPage -> MapPage ref chain is not wired on the first frames, so
      // retry briefly rather than silently doing nothing.
      if (!map) { if (tries++ < 20) setTimeout(attempt, 100); return; }
      fn(map);
    };
    attempt();
  }, []);

  const resizeOnly = useCallback(() => { withMap((map) => map.resize()); }, [withMap]);

  const refit = useCallback(() => {
    withMap((map) => {
      map.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    });
  }, [withMap]);

  // DESKTOP: the panel animates between full-width (hero slides) and 56% over
  // 0.45s. Without this the canvas keeps its previous width for the whole of
  // the rest of the reel and the projection is wrong: this is what made the
  // viewport look broken after the first slide. Resize across the transition,
  // then refit once it has settled.
  useEffect(() => {
    if (window.innerWidth < 600) return;
    const t1 = setTimeout(resizeOnly, 100);
    const t2 = setTimeout(resizeOnly, 300);
    const t3 = setTimeout(refit, 520);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isHeroStep, resizeOnly, refit]);

  // MOBILE: the fixed 40dvh strip is short and wide, and dvh shifts as browser
  // chrome shows and hides, so reconcile after first paint and on any box
  // change. Debounced to coalesce the transition frames.
  useEffect(() => {
    if (window.innerWidth >= 600) return;
    const raf = requestAnimationFrame(refit);
    const settle = setTimeout(refit, 350);
    let debounce;
    let ro;
    const el = mapPanelRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => { clearTimeout(debounce); debounce = setTimeout(refit, 150); });
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(debounce);
      if (ro) ro.disconnect();
    };
  }, [refit]);

  // Mobile fullscreen toggle: the box changes height, so reconcile immediately
  // (tracking the animation start) and again after the 0.3s transition.
  useEffect(() => {
    if (window.innerWidth >= 600) return;
    refit();
    const t = setTimeout(refit, 350);
    return () => clearTimeout(t);
  }, [mapFullscreen, refit]);

  // A window resize changes the panel's box on both platforms.
  useEffect(() => {
    let t;
    const onResize = () => { clearTimeout(t); t = setTimeout(refit, 180); };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, [refit]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (window.innerWidth < 600) {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.index);
            if (entry.isIntersecting) visibleSet.current.add(idx);
            else visibleSet.current.delete(idx);
          });
          if (visibleSet.current.size > 0) setActiveIndex(Math.max(...visibleSet.current));
        } else {
          const nearestIdx = getNearestCardIndex();
          if (nearestIdx != null) setActiveIndex(nearestIdx);
        }
      },
      { root: window.innerWidth < 600 ? null : scrollContainerRef.current, rootMargin: "0px 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, [getNearestCardIndex]);

  const progress = (activeIndex / (STEPS.length - 1)) * 100;

  // Map label reflects whatever the active slide is currently showing.
  const mapLabel = (() => {
    if (activeStep?.name === "frame") {
      const name = rowIdx === -1 ? "All adults" : FRAME_CUTS[cutIdx]?.rows[rowIdx];
      const opt = FRAME_OPTS.find((o) => o.key === optKey);
      return { text: `${opt?.label ?? ""}${name ? ` · ${name}` : ""}`, color: opt?.color };
    }
    if (activeStep?.name === "intro") {
      const geo = INTRO_GEO_LEVELS.find((g) => g.key === introGeo);
      return { text: `Economic approval: net score by ${geo?.label ?? "district"} · ${WAVE_LABELS[activeWave]}`, color: null };
    }
    if (activeStep?.name === "regulation-appetite") {
      if (regView === "gain") {
        return { text: "Biggest gain by district, April to July 2026", color: TEAL };
      }
      const f = REG_FILTERS.find((x) => x.key === regFilter);
      return { text: `"${f?.label ?? ""}", % of adults by district`, color: f?.color };
    }
    if (activeStep?.name === "who-regulates" || activeStep?.name === "texas") {
      return { text: activeStep.mapLabel, color: "#4a3aa7" };
    }
    return { text: activeStep?.mapLabel, color: null };
  })();

  return (
    <Box sx={{ bgcolor: BG, height: { xs: "auto", sm: "100vh" }, overflow: { xs: "visible", sm: "hidden" } }}>
      {/* Minimal chrome, matching the self-driving reel: a hairline progress
          line and a floating exit, with no branding bar. The bar it replaces
          was 47px tall, which is why every offset below is now 0 rather than
          47px and the map runs the full height of the viewport. */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100, display: "flex", alignItems: "center", px: 2, py: 1, pointerEvents: "none" }}>
        <Box role="progressbar" aria-valuenow={Math.round(progress)} sx={{ height: 3, position: "absolute", top: 0, left: 0, bgcolor: TEAL, width: `${progress}%`, transition: "width 0.4s ease" }} />
        {/* Held back until the last slide: on every other one it is an invitation
            to leave before the reel has made its case. */}
        {activeIndex === STEPS.length - 1 && (
        <Box onClick={() => exitHighlights(navigate)} sx={{ ml: "auto", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", cursor: "pointer", pointerEvents: "auto", color: "rgba(15,23,42,0.45)", bgcolor: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)", border: "1px solid rgba(15,23,42,0.06)", "&:hover": { color: TEAL }, transition: "all 0.15s ease" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </Box>
        )}
      </Box>

      <Box ref={mapPanelRef} sx={{ position: "fixed", top: 0, left: { xs: 0, sm: "auto" }, right: 0, width: { xs: "100%", sm: isHeroStep ? "100%" : "50%", md: isHeroStep ? "100%" : "52%" }, height: { xs: mapFullscreen ? "100dvh" : "40dvh", sm: "100vh" }, zIndex: { xs: mapFullscreen ? 1300 : 5, sm: 5 }, overflow: "hidden", borderLeft: { sm: isHeroStep ? "none" : "0.5px solid rgba(15,23,42,0.06)" }, bgcolor: "background.paper", transition: "width 0.45s ease, height 0.3s ease, top 0.3s ease" }}>
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", filter: isHeroStep ? "blur(10px) saturate(0.8)" : "none", transform: isHeroStep ? "scale(1.04)" : "none", transition: "filter 0.5s ease, transform 0.5s ease" }}>
            <AppPanelWrapper ref={mainRef} interactive={mapFullscreen} />
          </Box>

          {isHeroStep && (
            <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.3)", backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.5) 100%)", pointerEvents: "auto", zIndex: 2 }} />
          )}

          <Box sx={{ position: "absolute", inset: 0, zIndex: 8, cursor: "default", pointerEvents: "none" }} />

          <Box sx={{ position: "absolute", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", opacity: mapReady ? 0 : 1, pointerEvents: mapReady ? "none" : "auto", transition: "opacity 0.6s ease" }}>
            <CircularProgress size={28} thickness={3.5} sx={{ color: TEAL }} />
          </Box>

          {activeStep?.name === "who-regulates" && !isHeroStep && <WhoRegulatesLegend />}

          {activeStep?.name === "regulation-appetite" && regView === "gain" && !isHeroStep && <RegGainLegend />}

          {/* Movers are a national-baseline reading, so they appear only while
              "All adults" is selected (rowIdx -1) rather than a demographic cut. */}
          {activeStep?.name === "frame" && rowIdx === -1 && !isHeroStep && (
            <FrameMoversOverlay
              movers={FRAME_MOVERS[optKey]}
              optLabel={FRAME_OPTS.find((o) => o.key === optKey)?.label}
              highlightId={highlightId}
              onHighlight={handleHighlight}
            />
          )}

          {activeStep?.showWaveToggle && !isHeroStep && (
            <WaveToggle current={activeWave} onChange={handleWaveChange} />
          )}
          {activeStep?.showIntroGeoToggle && !isHeroStep && (
            <IntroGeoToggle current={introGeo} onChange={handleIntroGeo} />
          )}

          <Box sx={{ position: "absolute", top: { xs: 54, sm: 12 }, left: 12, zIndex: 10, pointerEvents: { xs: isHeroStep ? "none" : "auto", sm: "none" }, opacity: isHeroStep ? 0 : 1, transition: "opacity 0.25s ease", maxWidth: "calc(100% - 130px)" }}>
            <Box
              key={mapLabel.text}
              onClick={() => setLabelExpanded((v) => !v)}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.65, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)", border: `1px solid ${TEAL}20`, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", cursor: { xs: "pointer", sm: "default" }, WebkitTapHighlightColor: "transparent" }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: mapLabel.color || `linear-gradient(135deg, ${TEAL} 0%, ${ACCENT} 100%)`, flexShrink: 0 }} />
              <Typography sx={{ display: { xs: labelExpanded ? "inline" : "none", sm: "inline" }, fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F, letterSpacing: "0.01em", lineHeight: 1.3 }}>
                {mapLabel.text}
              </Typography>
            </Box>
          </Box>

          <Box
            onClick={() => setMapFullscreen((f) => !f)}
            sx={{ display: { xs: "flex", sm: "none" }, position: "absolute", bottom: 12, right: 12, zIndex: 20, width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}33`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }}
          >
            {mapFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 1, position: "fixed", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1050, transition: "opacity 0.4s ease" }}>
        {STEPS.map((s, i) => (
          <Box key={s.name} onClick={() => scrollToIndex(i)} title={s.headline?.slice(0, 60)} sx={{ width: activeIndex === i ? 8 : 5, height: activeIndex === i ? 8 : 5, borderRadius: "50%", bgcolor: activeIndex === i ? TEAL : `${TEAL}30`, transition: "all 0.25s ease", cursor: "pointer", "&:hover": { bgcolor: TEAL, transform: "scale(1.35)" } }} />
        ))}
      </Box>

      {activeIndex === 0 && (
        <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: "calc(54px + env(safe-area-inset-bottom) + 10px)", left: "50%", transform: "translateX(-50%)", zIndex: 1040, alignItems: "center", gap: 0.75, px: 1.75, py: 0.6, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}22`, boxShadow: "0 2px 12px rgba(0,0,0,0.10)", pointerEvents: "none" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>Scroll</Typography>
          <Box sx={{ display: "flex", animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.6 }, "50%": { transform: "translateY(3px)", opacity: 1 } } }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </Box>
        </Box>
      )}

      <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, height: 34, bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", borderTop: "0.5px solid rgba(15,23,42,0.06)", zIndex: 1050, alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Box onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === 0 ? "default" : "pointer", opacity: activeIndex === 0 ? 0.25 : 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Prev</Typography>
        </Box>
        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: MUTED, fontFamily: F, letterSpacing: "0.05em" }}>{activeIndex + 1} / {STEPS.length}</Typography>
        <Box onClick={() => scrollToIndex(Math.min(STEPS.length - 1, activeIndex + 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", opacity: activeIndex === STEPS.length - 1 ? 0.25 : 1 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Next</Typography>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
      </Box>

      <Box ref={scrollContainerRef} sx={{ mr: { xs: 0, sm: isHeroStep ? 0 : "50%", md: isHeroStep ? 0 : "52%" }, pt: { xs: "40dvh", sm: 0 }, pb: { xs: "54px", sm: 0 }, height: { xs: "auto", sm: "100vh" }, overflowY: { xs: "visible", sm: "scroll" }, scrollSnapType: { xs: "none", sm: "y mandatory" }, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            authState={authState}
            cutIdx={cutIdx}
            onCutChange={handleCutChange}
            highlightId={highlightId}
            onHighlight={handleHighlight}
            optKey={optKey}
            onOptChange={handleOptChange}
            rowIdx={rowIdx}
            onRowChange={handleRowChange}
            regFilter={regFilter}
            onRegFilterChange={handleRegFilter}
            regView={regView}
            onRegViewChange={handleRegView}
            onScrollNext={() => scrollToIndex(i + 1)}
          />
        ))}
      </Box>
    </Box>
  );
}
